import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useRazorpay } from "react-razorpay";
import {
  Search, ShoppingCart, MapPin, ChevronRight, ChevronLeft, ChevronDown, Gavel, Heart, UserRound,
  Menu, X, Plus, ArrowLeft, Minus, Trash2, Check, Star, Store, Zap, Clock, Download, Apple as AppleIcon,
  Smartphone, Laptop, Watch, Tablet, Headphones, Gamepad2, Camera, Home as HomeIcon, Percent, LayoutGrid, Package,
  Truck, RotateCcw, ShieldCheck, BadgeCheck, LoaderCircle, LogOut, Wallet, Tag, ZoomIn, Sun, Moon, Clock3, Landmark,
  ArrowUpRight, Navigation as NavIcon, Pencil, Save, Gift, Copy, MessageCircle, Link2,
} from "lucide-react";
import { api, apiError, cardProduct, restoreSession, PLACEHOLDER_IMAGE, resolveMediaUrl } from "@/api";
import { speakHindi } from "@/lib/adminFeedback";
import { QC_CHECKS, QC_GRADES, gradeLabel } from "@/lib/qc";
import AdminWorkspace from "@/components/AdminWorkspace";
import AccountExtras from "@/components/AccountExtras";
import { CustomerOrderDetail, CustomerOrders } from "@/components/OrderViews";
import { CustomerLiveChat } from "@/components/LiveChat";
import { AuctionExperience } from "@/components/AuctionExperience";
import { ManualUpiPayment } from "@/components/ManualUpiPayment";
import SellPageNew from "@/components/SellPage";
import AuctionWinnerPayment from "@/components/AuctionWinnerPayment";
import SellRequestFlow from "@/components/SellRequestFlow";
import DeliveryAddressModal from "@/components/DeliveryAddressModal";
import "@/App.css";
import "@/category-hub.css";
import "@/futuristic-2080.css";
import "@/premium-iphone.css";
import { FuturisticHome } from "@/components/FuturisticHome";

const money = (value = 0) => `₹${Number(value).toLocaleString("en-IN")}`;

const NAV_CATEGORIES = [
  { label: "Mobiles", slug: "mobiles", icon: Smartphone },
  { label: "Laptops", slug: "laptops", icon: Laptop },
  { label: "Smart Watches", slug: "smartwatch", icon: Watch },
  { label: "Tablets", slug: "tablets", icon: Tablet },
  { label: "Accessories", slug: "accessories", icon: LayoutGrid },
  { label: "Gaming", slug: "gaming", icon: Gamepad2 },
  { label: "Cameras", slug: "cameras", icon: Camera },
  { label: "Audio", slug: "audio", icon: Headphones },
  { label: "Home & Living", slug: "home-living", icon: HomeIcon },
];
const CIRCLE_CATEGORIES = [
  { label: "Mobiles", slug: "mobiles", icon: Smartphone },
  { label: "Laptops", slug: "laptops", icon: Laptop },
  { label: "Smart Watches", slug: "smartwatch", icon: Watch },
  { label: "Tablets", slug: "tablets", icon: Tablet },
  { label: "Accessories", slug: "accessories", icon: LayoutGrid },
  { label: "Gaming", slug: "gaming", icon: Gamepad2 },
  { label: "Cameras", slug: "cameras", icon: Camera },
  { label: "Audio", slug: "audio", icon: Headphones },
  { label: "Wearables", slug: "smartwatch", icon: Watch },
  { label: "Home & Living", slug: "home-living", icon: HomeIcon },
  { label: "Deals", slug: "", icon: Percent },
  { label: "More", slug: "", icon: LayoutGrid },
];
const HERO_PHONE = "/media/hero-phone.jpeg";
const PREOWNED = "/media/preowned.jpeg";

const BADGES = ["Bestseller", "New Launch", "Hot Deal", "Assured", "Top Rated", "Value"];
const FEATURED_ORDER = ["iphone", "samsung", "macbook", "boat-airdopes-141", "pixel-7", "nothing-phone-2"];
const rateFor = (id = "") => {
  const seed = [...String(id)].reduce((total, char) => total + char.charCodeAt(0), 0);
  return { stars: (4 + (seed % 9) / 10).toFixed(1), count: `${(4 + (seed % 12))}.${seed % 9}K` };
};

function Brand({ small = false }) {
  return (
    <div className={`brand ${small ? "brand-sm" : ""}`}>
      <span className="brand-mark dm-glow"><b>DM</b></span>
      <span className="brand-text"><b>DMMobile Shop</b>{!small && <small>Trusted Mobile Store</small>}</span>
    </div>
  );
}
function Loading({ label = "Loading deals…" }) {
  return <div className="empty-state" data-testid="loading-state"><LoaderCircle className="spin" size={30} /><h3>{label}</h3></div>;
}

function GoogleCallback({ setUser }) {
  const navigate = useNavigate(); const location = useLocation();
  const [message, setMessage] = useState("Completing Google sign-in…");
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("code");
    if (!code) { setMessage("Google sign-in was cancelled or could not be completed."); return; }
    const redirectUri = `${window.location.origin}/auth/google`;
    api.post("/api/auth/google", { code, redirect_uri: redirectUri }).then(({ data }) => { setUser(data); toast.success("Google sign-in complete"); navigate(data.role === "admin" ? "/admin" : "/"); }).catch((error) => { setMessage(apiError(error)); });
  }, [location.search, navigate, setUser]);
  return <main className="auth-page"><Link to="/" data-testid="google-callback-brand-link"><Brand /></Link><section className="auth-card google-callback" data-testid="google-callback-state"><LoaderCircle className="spin" size={28} /><h1>{message}</h1>{message !== "Completing Google sign-in…" && <Link to="/login" className="primary-btn" data-testid="google-callback-return-login">Return to sign in</Link>}</section></main>;
}

function Topbar({ cartCount, wishCount = 0, user, onSearch, onLogout, onMenu }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(() => { try { return JSON.parse(localStorage.getItem("dm-search-history")) || []; } catch { return []; } });
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [deliveryAddr, setDeliveryAddr] = useState(() => { try { return JSON.parse(localStorage.getItem("dm-delivery")) || null; } catch { return null; } });

  // Auto-populate delivery address from user's saved account address
  useEffect(() => {
    if (user?.addresses?.[0]) {
      const a = user.addresses[0];
      const addr = { pincode: a.postal_code || "", city: a.city || "", area: a.line1 || "", landmark: "" };
      setDeliveryAddr(addr);
      localStorage.setItem("dm-delivery", JSON.stringify(addr));
    }
  }, [user?.addresses]);

  const doSearch = (q) => {
    const t = q.trim(); if (!t) return;
    const updated = [t, ...history.filter((h) => h !== t)].slice(0, 8);
    setHistory(updated); localStorage.setItem("dm-search-history", JSON.stringify(updated));
    onSearch(t); navigate(`/category?search=${encodeURIComponent(t)}`); setShowHistory(false); setQuery(t);
  };
  const submit = (e) => { if (e.key === "Enter") doSearch(query); if (e.key === "Escape") setShowHistory(false); };
  const removeHistItem = (item, e) => { e.stopPropagation(); const updated = history.filter((h) => h !== item); setHistory(updated); localStorage.setItem("dm-search-history", JSON.stringify(updated)); };
  const addrDisplay = deliveryAddr ? (`${deliveryAddr.city || ""} ${deliveryAddr.pincode || ""}`.trim() || "New Delhi 110085") : "New Delhi 110085";

  return (
    <>
    <header className="topbar">
      <div className="marketplace-trust-strip" data-testid="marketplace-trust-strip"><span><ShieldCheck size={12} /> 100% Genuine Products</span><span><Wallet size={12} /> Secure Payments</span><span><Truck size={12} /> Fast Shipping</span><span><RotateCcw size={12} /> Easy Returns</span><Link to="/my-orders" data-testid="trust-strip-track-order">Track Order</Link></div>
      <div className="topbar-row">
        <button className="icon-btn mobile-only" onClick={onMenu} data-testid="mobile-menu-button"><Menu size={20} /></button>
        <Link to="/" data-testid="brand-home-link"><Brand /></Link>
        <div className="search-wrapper">
          <label className="search">
            <Search size={18} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={submit} onFocus={() => setShowHistory(true)} onBlur={() => setTimeout(() => setShowHistory(false), 200)} data-testid="store-search-input" placeholder="Search for iPhones, Laptops, Smartwatches..." />
          </label>
          {showHistory && history.length > 0 && (
            <div className="search-history-drop" data-testid="search-history-dropdown">
              <div className="sh-head"><Clock3 size={12} /> Recent Searches</div>
              {history.map((item) => (
                <div key={item} className="sh-item" onMouseDown={() => doSearch(item)}>
                  <Search size={12} /><span>{item}</span>
                  <button className="sh-remove" onMouseDown={(e) => removeHistItem(item, e)}><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="deliver" data-testid="deliver-to-button" onClick={() => setShowAddrModal(true)}><MapPin size={16} /><span>Deliver to<b>{addrDisplay}</b></span><ChevronDown size={13} /></button>
        <div className="top-actions">
          <Link className="top-link cart-top-link" to="/cart" data-testid="cart-header-button"><span className="ic cart-ic"><img src="https://customer-assets-39nsmqrw.emergentagent.net/job_dashboard-design-20/artifacts/eh1bxhw2_WhatsApp%20Image%202026-09-29%20at%208.10.52%20PM.jpeg" alt="Cart" className="topbar-cart-img" />{cartCount > 0 && <i>{cartCount}</i>}</span></Link>
          {user
            ? <Link className="top-link acct" to="/account" data-testid="account-profile-link"><UserRound size={20} /><span className="acct-copy"><small>Hi,</small><b>{user.name.split(" ")[0]}</b></span></Link>
            : <Link className="top-link acct" to="/login" data-testid="account-login-link"><UserRound size={20} /><span className="acct-copy"><small>Login /</small><b>Register</b></span></Link>}
          <Link className="sell-btn" to="/sell" data-testid="sell-button">Sell on DM Mobile</Link>
        </div>
      </div>
      <nav className="cat-nav">
        <button className="all-cats" onClick={() => navigate("/category")} data-testid="all-categories-button"><Menu size={15} /> All Categories</button>
        {NAV_CATEGORIES.map((category) => (
          <Link key={category.label} to={category.slug ? `/category?category=${category.slug}` : "/category"} data-testid={`nav-cat-${category.slug || category.label.toLowerCase()}`}>{category.label}</Link>
        ))}
        <Link to="/category" className="more-cat" data-testid="nav-cat-more">More <ChevronDown size={12} /></Link>
      </nav>
    </header>
    {showAddrModal && <DeliveryAddressModal onClose={() => setShowAddrModal(false)} onSave={(addr) => { localStorage.setItem("dm-delivery", JSON.stringify(addr)); setDeliveryAddr(addr); }} current={deliveryAddr} />}
    </>
  );
}

function FooterBar() {
  return (
    <footer className="footer-bar">
      <Brand small />
      <div className="footer-feats">
        {[[Truck, "Free Delivery", "All Over India"], [RotateCcw, "Easy Returns", "7-Day Policy"], [ShieldCheck, "Secure Payments", "UPI, Cards, Wallet"], [BadgeCheck, "Verified Sellers", "100% Trusted"]].map(([Icon, title, sub]) => (
          <div className="footer-feat" key={title}><Icon size={20} /><span><b>{title}</b><small>{sub}</small></span></div>
        ))}
      </div>
      <div className="footer-app"><nav className="footer-policies" data-testid="footer-policy-links"><Link to="/policies/shipping" data-testid="policy-shipping-link">Shipping</Link><Link to="/policies/returns" data-testid="policy-returns-link">Returns & Refunds</Link><Link to="/policies/privacy" data-testid="policy-privacy-link">Privacy</Link><Link to="/policies/terms" data-testid="policy-terms-link">Terms</Link></nav><small className="powered-by" data-testid="powered-by-label">Powered by DMobileMart-Muskan</small>
        <button className="download-btn" data-testid="download-app-button"><Download size={16} /> Download Our App</button>
        <div className="app-badges"><span>▶ Shop Anytime</span><AppleIcon size={16} /></div>
      </div>
    </footer>
  );
}

function ProductCard({ product, onAdd, onWish, badge }) {
  if (!product) return null;
  const rating = rateFor(product.id); const outOfStock = product.stock <= 0;
  return (
    <article className="product-card" data-testid={`product-card-${product.id}`}>
      {outOfStock ? <span className="stock-badge out" data-testid={`out-of-stock-${product.id}`}>OUT OF STOCK</span> : badge && <span className="card-badge">{badge}</span>}
      <div className="product-media-wrap">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} className="product-media">
          <img src={product.image} alt={product.name} loading="lazy" />
        </Link>
        <button className="wish" onClick={() => onWish(product.id)} data-testid={`wishlist-${product.id}`} aria-label={`Save ${product.name}`}><Heart size={16} /></button>
      </div>
      <div className="product-info">
        <Link to={`/product/${product.id}`}><h4 className="pc-name">{product.name}</h4></Link>
        <p className="pc-sub">{product.sub}</p>
        <div className="rating-line"><span className="stars"><Star size={11} fill="currentColor" /> {rating.stars}</span><small>({rating.count})</small></div>
        <div className="price-row">
          <strong>{money(product.price)}</strong>
          {product.old > product.price && <del>{money(product.old)}</del>}
          {product.off && <em className="off-pill">{product.off}</em>}
        </div>
      </div>
      <button className="add-cart-btn" disabled={outOfStock} onClick={() => onAdd(product)} data-testid={`add-product-${product.id}`}>
        {outOfStock ? "Out of Stock" : <><ShoppingCart size={13} /> Add to Cart</>}
      </button>
    </article>
  );
}

function SectionTitle({ title, action = "View All", to = "/category", extra }) {
  return <div className="section-title"><h2>{title}</h2>{extra}<Link to={to} data-testid={`view-${title.toLowerCase().replaceAll(" ", "-")}-link`}>{action} <ChevronRight size={15} /></Link></div>;
}

function Countdown() {
  const [left, setLeft] = useState(12 * 3600 + 45 * 60 + 30);
  useEffect(() => { const timer = setInterval(() => setLeft((value) => (value > 0 ? value - 1 : 12 * 3600)), 1000); return () => clearInterval(timer); }, []);
  const pad = (value) => String(value).padStart(2, "0");
  return (
    <div className="countdown" data-testid="flash-countdown">
      <span>{pad(Math.floor(left / 3600))}</span>:<span>{pad(Math.floor((left % 3600) / 60))}</span>:<span>{pad(left % 60)}</span>
    </div>
  );
}

function StoreHome({ products, auctions, onAdd, onWish, common, announcements = [] }) {
  const [slide, setSlide] = useState(0);
  const [tab, setTab] = useState("All");
  const display = useMemo(() => [...products].sort((a, b) => (FEATURED_ORDER.indexOf(a.id) + 99) % 99 - (FEATURED_ORDER.indexOf(b.id) + 99) % 99), [products]);
  const primaryProduct = display[0] || null;
  const laptopProduct = display.find((product) => product?.category_slug === "laptops") || display[2] || primaryProduct;
  const heroDeals = display.slice(0, 3);
  const trending = display.filter((product) => tab === "All" || product.category_slug === tab.toLowerCase().replace(" ", ""));
  useEffect(() => { const timer = setInterval(() => setSlide((value) => (value + 1) % 4), 4000); return () => clearInterval(timer); }, []);
  if (!display.length) return <><Topbar {...common} /><main className="store-page"><Loading /></main><FooterBar /></>;

  return (
    <>
      <Topbar {...common} />
      {announcements.length > 0 && <section className="announcement-strip" data-testid="website-announcement-strip">
        <div className="announcement-label" data-testid="website-announcement-label"><Smartphone size={14} /><span>LIVE UPDATE</span></div>
        <div className="announcement-viewport">
          <div className="announcement-track">
            <div className="announcement-group">
              {announcements.slice(0, 6).map((ann, idx) => (
                <Link to="/category" key={ann.id} data-testid={`announcement-${ann.id}`} className={idx === 0 ? "ann-new" : ""}>
                  {idx === 0 && <span className="ann-new-badge">NEW</span>}
                  <Smartphone size={13} /><b>{ann.title.replace("New arrival: ", "")}</b>
                  {ann.description && <small>{ann.description}</small>}
                </Link>
              ))}
            </div>
            <div className="announcement-group announcement-copy" aria-hidden="true">
              {announcements.slice(0, 6).map((ann, idx) => (
                <span key={`copy-${ann.id}`}>
                  {idx === 0 && <span className="ann-new-badge">NEW</span>}
                  <Smartphone size={13} /><b>{ann.title.replace("New arrival: ", "")}</b>
                  {ann.description && <small>{ann.description}</small>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>}
      <main className="store-page marketplace-home">
        <div className="marketplace-stage">
          <aside className="marketplace-category-rail" data-testid="marketplace-category-rail"><b>Top Categories</b>{NAV_CATEGORIES.slice(0, 7).map((category) => { const Icon = category.icon; return <Link key={category.slug} to={`/category?category=${category.slug}`} data-testid={`marketplace-rail-${category.slug}`}><Icon size={15} /><span>{category.label}</span></Link>; })}<Link to="/auctions" className="rail-auction" data-testid="marketplace-rail-auctions"><Gavel size={15} /><span>Live Auction</span></Link><Link to="/category?sort=price_desc" className="rail-deals" data-testid="marketplace-rail-deals"><Percent size={15} /><span>Deals & Offers</span></Link></aside>
          <section className="hero-grid">
          <div className="hero-banner" data-testid="hero-banner">
            <div className="hero-copy">
              <span className="eyebrow">PREMIUM TECH. SMARTER PRICES.</span>
              <h1>Upgrade<br /><span className="grad">Your World</span></h1>
              <p className="hero-sub">New &amp; Pre-owned Devices • Verified Sellers • Best Deals</p>
              <div className="hero-badge"><b>{primaryProduct?.name || "DM Mobile picks"}</b><span>Up to <em>40% OFF</em></span></div>
              <div className="hero-cta">
                <Link to="/category" className="primary-btn" data-testid="hero-shop-now">Shop Now <ChevronRight size={15} /></Link>
                <Link to="/sell" className="ghost-btn" data-testid="hero-sell-device">Sell Your Device</Link>
              </div>
              <div className="hero-trust">
                {[[ShieldCheck, "Verified Products"], [Wallet, "Secure Payments"], [RotateCcw, "7-Day Return"], [BadgeCheck, "Trusted Sellers"]].map(([Icon, label]) => (
                  <span key={label}><Icon size={15} /> {label}</span>
                ))}
              </div>
            </div>
            <img src={HERO_PHONE} alt="Featured device" className="hero-phone" />
            <button className="hero-arrow left" onClick={() => setSlide((slide + 3) % 4)} data-testid="hero-prev"><ChevronLeft size={18} /></button>
            <button className="hero-arrow right" onClick={() => setSlide((slide + 1) % 4)} data-testid="hero-next"><ChevronRight size={18} /></button>
            <div className="hero-dots">{[0, 1, 2, 3].map((dot) => <i key={dot} className={dot === slide ? "on" : ""} />)}</div>
          </div>
          <aside className="hot-deals" data-testid="hot-deals">
            <div className="hot-head"><b>Today's Hot Deals</b><Link to="/category?sort=price_desc" data-testid="hot-deals-view-all">View All <ChevronRight size={13} /></Link></div>
            {heroDeals.map((product) => (
              <div className="hot-item" key={product.id} data-testid={`hot-deal-${product.id}`}>
                <img src={product.image} alt={product.name} />
                <div className="hot-copy"><b>{product.name}</b><small>{product.sub}</small><strong>{money(product.price)}</strong><em>{product.off}</em></div>
                <button onClick={() => onAdd(product)} data-testid={`hot-add-${product.id}`}><ShoppingCart size={15} /></button>
              </div>
            ))}
          </aside>
          </section>
        </div>

        <section className="circle-row">
          {CIRCLE_CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <Link key={category.label} to={category.slug ? `/category?category=${category.slug}` : "/category"} className="circle-cat" data-testid={`circle-${category.label.toLowerCase().replace(/ & | /g, "-")}`}>
                <span className="circle-icon"><Icon size={22} /></span>
                <small>{category.label}</small>
              </Link>
            );
          })}
        </section>
        <section className="promo-row">
          <div className="promo promo-a" data-testid="promo-iphone">
            <div><b>Biggest iPhone Deals</b><span>Up to <em>40% OFF</em></span><Link to="/category?category=mobiles" className="promo-btn">Shop iPhones <ChevronRight size={13} /></Link></div>
            <img src={primaryProduct?.image || HERO_PHONE} alt="iPhone deals" />
          </div>
          <div className="promo promo-b" data-testid="promo-laptops">
            <div><b>Laptops for Work &amp; Play</b><span>Top Brands. Great Prices.</span><Link to="/category?category=laptops" className="promo-btn">Explore Laptops <ChevronRight size={13} /></Link></div>
            <img src={laptopProduct?.image || HERO_PHONE} alt="Laptops" />
          </div>
          <div className="promo promo-c" data-testid="promo-preowned">
            <div><b>Certified Pre-Owned</b><span>Same Performance. Better Value.</span>
              <div className="promo-ticks"><span><Check size={11} /> Quality Checked</span><span><Check size={11} /> 6 Months Warranty</span><span><Check size={11} /> Best Price</span></div>
              <Link to="/category" className="promo-btn">Shop Pre-Owned <ChevronRight size={13} /></Link>
            </div>
            <img src={PREOWNED} alt="Pre-owned devices" />
          </div>
        </section>

        <section className="trending-wrap">
          <div className="trending-main">
            <div className="section-title trending-head">
              <h2>Trending Products</h2>
              <div className="pill-tabs">
                {["All", "Mobiles", "Laptops", "Smart Watches", "Accessories", "Tablets", "Gaming"].map((label) => (
                  <button key={label} className={tab === label ? "on" : ""} onClick={() => setTab(label)} data-testid={`trending-tab-${label.toLowerCase().replace(" ", "-")}`}>{label}</button>
                ))}
              </div>
              <Link to="/category" data-testid="trending-view-all">View All <ChevronRight size={15} /></Link>
            </div>
            <div className="product-grid grid-5">
              {(trending.length ? trending : display).map((product, index) => (
                <ProductCard product={product} onAdd={onAdd} onWish={onWish} badge={BADGES[index % BADGES.length]} key={product.id} />
              ))}
            </div>
          </div>
          <aside className="why-choose" data-testid="why-choose">
            <b>Why Choose DM Mobile?</b>
            {[[BadgeCheck, "Verified Sellers", "Only trusted & verified sellers"], [ShieldCheck, "Quality Checked", "Every product inspected"], [Percent, "Best Prices", "Unbeatable deals on top brands"], [Wallet, "Secure Payments", "100% safe & encrypted"], [RotateCcw, "7-Day Returns", "Hassle-free returns"], [Headphones, "Dedicated Support", "We're here to help"]].map(([Icon, title, sub]) => (
              <div className="why-row" key={title}><span><Icon size={16} /></span><div><b>{title}</b><small>{sub}</small></div></div>
            ))}
          </aside>
        </section>

        <section className="flash-wrap">
          <SectionTitle title="Flash Deals" action="View All" to="/category?sort=price_desc" extra={<div className="flash-timer"><Clock size={14} /> Ends in <Countdown /></div>} />
          <div className="product-grid grid-6 flash-grid">
            {display.map((product, index) => (
              <article className="flash-card" key={product.id} data-testid={`flash-card-${product.id}`}>
                <span className="flash-off">{product.off}</span>
                <Link to={`/product/${product.id}`}><img src={product.image} alt={product.name} /></Link>
                <b>{product.name}</b><strong>{money(product.price)}</strong>
                <button onClick={() => onAdd(product)} data-testid={`flash-add-${product.id}`}>Grab Deal</button>
              </article>
            ))}
          </div>
        </section>

        <section className="lower-grid">
          <div className="brands-panel">
            <SectionTitle title="Top Brands" action="View All" to="/category" />
            <div className="brand-row">
              {["Apple", "Samsung", "OnePlus", "Mi", "boAt"].map((brand) => (
                <Link to="/category" className="brand-chip" key={brand} data-testid={`brand-${brand.toLowerCase()}`}>{brand}</Link>
              ))}
            </div>
          </div>
          <div className="recent-panel">
            <SectionTitle title="Recently Viewed" action="View All" to="/category" />
            <div className="recent-row">
              {display.slice(0, 5).map((product) => (
                <Link to={`/product/${product.id}`} className="recent-item" key={product.id} data-testid={`recent-${product.id}`}><img src={product.image} alt={product.name} /></Link>
              ))}
            </div>
          </div>
        </section>

        {auctions[0] && (
          <section className="auction-teaser" data-testid="auction-teaser">
            <div className="auction-teaser-copy"><span className="live-pill">LIVE AUCTION</span><h3>{auctions[0].product?.name}</h3><small>Current Bid</small><strong>{money(auctions[0].current_bid)}</strong><Link to="/auctions" className="primary-btn" data-testid="auction-teaser-bid">Bid Now <Gavel size={14} /></Link></div>
            <img src={auctions[0].product?.image} alt="Auction" />
          </section>
        )}
      </main>
      <FooterBar />
      <BottomNav {...common} />
    </>
  );
}

function BottomNav({ active = "Home", cartCount = 0, orderCount = 0, activeOrderCount = 0, wishCount = 0 }) {
  const oc = orderCount || activeOrderCount;
  const items = [
    ["Home", "/", HomeIcon, null],
    ["Category", "/category", LayoutGrid, null],
    ["Cart", "/cart", ShoppingCart, cartCount],
    ["Orders", "/my-orders", Package, oc],
    ["Account", "/account", UserRound, null],
  ];
  return (
    <nav className="bottom-nav" data-testid="mobile-bottom-nav">
      {items.map(([label, to, Icon, badge]) => (
        <Link
          className={`${active === label ? "active" : ""} ${label === "Cart" ? "float-cart-btn" : ""}`}
          to={to}
          key={label}
          data-testid={`bottom-nav-${label.toLowerCase().replace(" ", "-")}`}
        >
          <span className="mobile-nav-icon">
            {label === "Cart" ? (
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_dashboard-design-20/artifacts/eh1bxhw2_WhatsApp%20Image%202026-09-29%20at%208.10.52%20PM.jpeg"
                alt="Cart"
                className="cart-nav-img"
              />
            ) : (
              <Icon size={19} />
            )}
            {badge > 0 && (
              <b data-testid={`mobile-nav-${label.toLowerCase()}-count`} className={label === "Orders" ? "order-badge" : ""}>
                {badge > 9 ? "9+" : badge}
              </b>
            )}
          </span>
          {label !== "Cart" && <span>{label}</span>}
        </Link>
      ))}
    </nav>
  );
}

function QCReport({ product }) {
  const [defectsOnly, setDefectsOnly] = useState(false);
  const status = product.qc_status || {};
  const entries = QC_CHECKS.map((check) => [check, status[check] || "unknown"]);
  const checked = entries.filter(([, value]) => value !== "unknown");
  if (!checked.length) return null;
  const passed = checked.filter(([, value]) => value === "pass").length;
  const failed = checked.filter(([, value]) => value === "fail").length;
  const shown = defectsOnly ? entries.filter(([, value]) => value === "fail") : entries.filter(([, value]) => value !== "unknown");
  return (
    <section className="qc-report" data-testid="qc-report">
      <div className="qc-report-head">
        <div><span className="eyebrow">QUALITY CHECK REPORT</span><h3><ShieldCheck size={16} /> {gradeLabel(product.qc_grade)} · {checked.length}-Point Inspection</h3></div>
        <label className="qc-defects-toggle" data-testid="qc-defects-toggle"><input type="checkbox" checked={defectsOnly} onChange={(event) => setDefectsOnly(event.target.checked)} /> Show defects only</label>
      </div>
      <div className="qc-metrics">
        <div className="qc-metric pass" data-testid="qc-pass-count"><b>{passed}</b><small>Passed</small></div>
        <div className="qc-metric fail" data-testid="qc-fail-count"><b>{failed}</b><small>Defects</small></div>
        <div className="qc-metric total"><b>{checked.length}</b><small>Checked</small></div>
      </div>
      <div className="qc-report-grid">
        {shown.map(([check, value]) => (
          <div className={`qc-report-item ${value}`} key={check} data-testid={`qc-report-${check}`}>
            <span className="qc-mark">{value === "pass" ? <Check size={13} /> : value === "fail" ? <X size={13} /> : "—"}</span>
            <span>{check}</span>
          </div>
        ))}
        {!shown.length && <p className="qc-no-defects" data-testid="qc-no-defects">कोई डिफेक्ट नहीं मिला — सभी चेक पास ✓</p>}
      </div>
    </section>
  );
}

function QCMiniBox({ product }) {
  const [open, setOpen] = useState(false);
  const status = product.qc_status || {};
  const entries = QC_CHECKS.map((c) => [c, status[c] || "unknown"]);
  const checked = entries.filter(([, v]) => v !== "unknown");
  if (!checked.length) return null;
  const passed = checked.filter(([, v]) => v === "pass").length;
  const failed = checked.filter(([, v]) => v === "fail").length;
  return (
    <div className={`qc-mini-box highlighted${open ? " open" : ""}`} data-testid="qc-mini-box">
      <button className="qc-mini-header" onClick={() => setOpen(!open)} data-testid="qc-mini-toggle">
        <ShieldCheck size={15} />
        <span className="qc-mini-grade">{gradeLabel(product.qc_grade)}</span>
        <span className="qc-mini-pass">{passed} Pass</span>
        {failed > 0 && <span className="qc-mini-fail">{failed} Issues</span>}
        <span className="qc-mini-pts">{checked.length}-pt Check</span>
        <ChevronDown size={13} className={`qc-mini-caret${open ? " rotated" : ""}`} />
      </button>
      {open && (
        <div className="qc-mini-grid" data-testid="qc-mini-grid">
          {checked.map(([check, v]) => (
            <div key={check} className={`qc-mini-item ${v}`}>
              {v === "pass" ? <Check size={10} /> : <X size={10} />}
              <span>{check}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductPage({ onAdd, onWish, common }) {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [zoomed, setZoomed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [deliveryAddr, setDeliveryAddr] = useState(() => { try { return JSON.parse(localStorage.getItem("dm-delivery")) || null; } catch { return null; } });
  const [activeImg, setActiveImg] = useState(0);
  const sliderTimer = useRef(null);
  const imgLenRef = useRef(0);

  useEffect(() => {
    setLoading(true); setSimilarProducts([]); setZoomed(false); setSelectedStorage(null); setActiveImg(0);
    api.get(`/api/products/${id}?_live=${Date.now()}`).then(async ({ data }) => {
      const selected = cardProduct(data);
      setProduct(selected);
      const response = await api.get(`/api/products?category=${encodeURIComponent(selected.category_slug)}&page_size=8`);
      setSimilarProducts(response.data.items.map(cardProduct).filter((item) => item.id !== selected.id).slice(0, 4));
    }).catch(() => toast.error("Product could not be found")).finally(() => setLoading(false));
  }, [id]);

  // Auto-slide when product has multiple images
  useEffect(() => {
    const count = product?.images?.length > 0 ? product.images.length : (product?.image ? 1 : 0);
    imgLenRef.current = count;
    clearInterval(sliderTimer.current);
    if (count > 1) {
      sliderTimer.current = setInterval(() => setActiveImg(prev => (prev + 1) % count), 3500);
    }
    return () => clearInterval(sliderTimer.current);
  }, [product]);

  const goToSlide = (i) => {
    setActiveImg(i);
    const count = imgLenRef.current;
    if (count > 1) {
      clearInterval(sliderTimer.current);
      sliderTimer.current = setInterval(() => setActiveImg(prev => (prev + 1) % count), 3500);
    }
  };

  if (loading) return <><Topbar {...common} /><main className="detail-page"><Loading /></main><FooterBar /></>;
  if (!product) return <Navigate to="/" replace />;
  const rating = rateFor(product.id);
  const outOfStock = product.stock <= 0;
  const storageVariants = product.storage_options?.length > 0 ? product.storage_options : [];
  const activeVariant = selectedStorage ? product.variants?.find((v) => v.attributes?.storage === selectedStorage) : null;
  const displayPrice = activeVariant?.price || product.price;
  const variantSku = activeVariant?.sku || null;
  const addrDisplay = deliveryAddr ? (`${deliveryAddr.city || ""} ${deliveryAddr.pincode || ""}`.trim() || "New Delhi 110085") : "New Delhi 110085";
  const images = product.images?.length > 0 ? product.images.map(resolveMediaUrl) : [product.image].filter(Boolean);
  return (
    <>
      <Topbar {...common} />
      <main className="detail-page">
        <Link to="/" className="back-link" data-testid="product-back-link"><ArrowLeft size={16} /> Back to store</Link>
        <div className="detail-grid">
          <div className="detail-image">
            <div className="photo-slider-wrap">
              <button className="image-zoom-trigger" onClick={() => setZoomed(true)} data-testid="detail-image-zoom-button" aria-label={`Zoom ${product.name}`}>
                <img key={activeImg} className="slider-img-animated" src={images[activeImg] || PLACEHOLDER_IMAGE} alt={product.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                <span><ZoomIn size={18} /> Zoom</span>
              </button>
              {images.length > 1 && (
                <>
                  <button className="slide-prev" onClick={() => goToSlide((activeImg - 1 + images.length) % images.length)} data-testid="slide-prev"><ChevronLeft size={18} /></button>
                  <button className="slide-next" onClick={() => goToSlide((activeImg + 1) % images.length)} data-testid="slide-next"><ChevronRight size={18} /></button>
                  <div className="photo-dots">
                    {images.map((_, i) => <button key={i} className={`photo-dot${activeImg === i ? " active" : ""}`} onClick={() => goToSlide(i)} data-testid={`photo-dot-${i}`} />)}
                  </div>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="photo-thumbnails" data-testid="photo-thumbnails">
                {images.map((img, i) => (
                  <button key={i} className={`photo-thumb${activeImg === i ? " active" : ""}`} onClick={() => goToSlide(i)} data-testid={`photo-thumb-${i}`}>
                    <img src={img} alt={`${product.name} ${i + 1}`} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => onWish(product.id)} data-testid="detail-wishlist" aria-label="Save product"><Heart /></button>
          </div>
          <div className="detail-copy">
            <span className="eyebrow">MOBILECART ASSURED</span>
            <h1>{product.name}</h1>
            <p className="subline">{product.sub} <span className="verified"><BadgeCheck size={13} /> Assured</span></p>
            <div className="rating"><Star size={14} fill="currentColor" /> {rating.stars} <span>({rating.count} Reviews)</span></div>
            <div className="price-line">
              <strong data-testid="product-display-price">{money(displayPrice)}</strong>
              <del>{money(product.old)}</del>
              {activeVariant && activeVariant.price !== product.price ? <em className="variant-price-tag">{selectedStorage}</em> : <em>{product.off}</em>}
            </div>
            <small className="emi">EMI from {money(Math.round(displayPrice / 20))}/month</small>
            {storageVariants.length > 0 ? (
              <div className="chips storage-chips" data-testid="storage-chips">
                {storageVariants.map((s) => {
                  const v = product.variants?.find((vt) => vt.attributes?.storage === s);
                  return (
                    <button key={s} className={`storage-chip${selectedStorage === s ? " selected" : ""}`} onClick={() => setSelectedStorage(selectedStorage === s ? null : s)} data-testid={`variant-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                      {s}{v?.price ? <small> · {money(v.price)}</small> : null}
                    </button>
                  );
                })}
                {product.color_options?.map((c) => (
                  <button key={c.name} className="storage-chip color-chip" data-testid={`variant-${c.name.toLowerCase().replace(/\s+/g, "-")}`} style={{ borderLeft: `3px solid ${c.hex}` }}>{c.name}</button>
                ))}
              </div>
            ) : (
              <div className="chips">{(product.variants?.length ? product.variants.map((variant) => variant.name) : ["Assured", outOfStock ? "Out of Stock" : "In Stock", "Fast Delivery"]).map((value) => <button key={value} data-testid={`variant-${String(value).toLowerCase().replaceAll(" ", "-")}`}>{value}</button>)}</div>
            )}
            <div className="delivery"><MapPin size={18} /><span>Deliver to <b>{addrDisplay}</b><small>Free delivery in 3-5 days</small></span><button type="button" className="addr-change-btn" onClick={() => setShowAddrModal(true)} data-testid="change-address-button">Change</button></div>
            <div className="seller"><Store size={20} /><div>Sold by <b>CellPoint Store</b></div><strong>Top Rated Seller<br />4.7 ★</strong></div>
            <QCMiniBox product={product} />
            <div className="detail-actions">
              <button className="ghost-btn" disabled={outOfStock} onClick={() => onAdd({ ...product, price: displayPrice }, false, variantSku)} data-testid="detail-add-to-cart">{outOfStock ? "Out of Stock" : "Add to Cart"}</button>
              <button className="primary-btn" disabled={outOfStock} onClick={() => onAdd({ ...product, price: displayPrice }, true, variantSku)} data-testid="detail-buy-now">{outOfStock ? "Currently unavailable" : "Buy Now"}</button>
            </div>
          </div>
        </div>
        <QCReport product={product} />
        {/*– Full QC section hidden per user request — compact QCMiniBox above Add to Cart shows summary –*/}
        {similarProducts.length > 0 && <section className="similar-products" data-testid="similar-products-section"><SectionTitle title="Similar Products" action="View All" to={`/category?category=${product.category_slug}`} /><div className="product-grid grid-4">{similarProducts.map((item, index) => <ProductCard product={item} onAdd={onAdd} onWish={onWish} badge={BADGES[index % BADGES.length]} key={item.id} />)}</div></section>}
      </main>
      {zoomed && <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`${product.name} image preview`} data-testid="product-image-zoom-modal"><button className="lightbox-backdrop" onClick={() => setZoomed(false)} aria-label="Close image preview" data-testid="product-image-zoom-backdrop" /><div className="lightbox-content"><img src={images[activeImg] || PLACEHOLDER_IMAGE} alt={`${product.name} enlarged`} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} /><button className="lightbox-close" onClick={() => setZoomed(false)} aria-label="Close image preview" data-testid="product-image-zoom-close"><X size={20} /></button></div></div>}
      {showAddrModal && <DeliveryAddressModal onClose={() => setShowAddrModal(false)} onSave={(addr) => { localStorage.setItem("dm-delivery", JSON.stringify(addr)); setDeliveryAddr(addr); }} current={deliveryAddr} />}
      <FooterBar />
      <BottomNav {...common} active="" />
    </>
  );
}

function CartPage({ cart, onAdd, onSet, onRemove, common }) {
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  return (
    <>
      <Topbar {...common} />
      <main className="cart-page">
        <div className="cart-head"><div><span className="eyebrow">YOUR BAG</span><h1>My Cart <small>({common.cartCount} items)</small></h1></div><Link to="/" data-testid="continue-shopping-link">Continue shopping <ChevronRight size={16} /></Link></div>
        <div className="cart-layout">
          <section className="cart-items">
            {cart.length ? cart.map((item) => (
              <div className="cart-item" key={`${item.id}-${item.variant_sku || "base"}`} data-testid={`cart-item-${item.id}`}>
                <img src={item.image || PLACEHOLDER_IMAGE} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                <div className="cart-item-copy"><b>{item.name}</b><small>{item.sub}</small><strong>{money(item.price)}</strong></div>
                <button onClick={() => onRemove(item.id)} data-testid={`remove-cart-${item.id}`} aria-label={`Remove ${item.name}`}><Trash2 size={16} /></button>
                <div className="quantity"><button onClick={() => onSet(item, item.quantity - 1)} data-testid={`decrease-${item.id}`}><Minus size={13} /></button><span>{item.quantity}</span><button onClick={() => onAdd(item)} data-testid={`increase-${item.id}`}><Plus size={13} /></button></div>
              </div>
            )) : <div className="empty-state"><ShoppingCart size={38} /><h3>Your cart is waiting</h3><Link to="/" className="primary-btn" data-testid="empty-cart-shop-button">Explore deals</Link></div>}
          </section>
          <aside className="summary">
            <h3>Order Summary</h3>
            <div><span>Subtotal</span><b>{money(total)}</b></div>
            <div><span>Platform fee</span><b>₹99</b></div>
            <div><span>Delivery</span><b className="green-text">FREE</b></div>
            <hr />
            <div className="total"><span>Total</span><strong>{money(total + (cart.length ? 99 : 0))}</strong></div>
            <Link to="/checkout" className="primary-btn full" data-testid="proceed-checkout-button">Proceed to Checkout <ChevronRight size={17} /></Link>
            <small className="secure"><ShieldCheck size={12} /> Secure checkout · 100% protected</small>
          </aside>
        </div>
      </main>
      <FooterBar />
      <BottomNav {...common} active="" />
    </>
  );
}

function AddressForm({ onDone }) {
  const [form, setForm] = useState({ label: "HOME", recipient_name: "", phone: "", line1: "", line2: "", city: "New Delhi", state: "Delhi", postal_code: "", country: "India" });
  const [busy, setBusy] = useState(false);
  const save = async (event) => { event.preventDefault(); setBusy(true); try { await api.post("/api/auth/me/addresses", form); await onDone(); toast.success("Address saved"); } catch (error) { toast.error(apiError(error)); } finally { setBusy(false); } };
  return (
    <form className="checkout-section address-form" onSubmit={save} data-testid="address-form">
      <h3>Delivery address</h3>
      {[["recipient_name", "Full name"], ["phone", "Phone number"], ["line1", "Address line"], ["city", "City"], ["state", "State"], ["postal_code", "PIN code"]].map(([key, label]) => (
        <input key={key} value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} placeholder={label} required data-testid={`address-${key}-input`} />
      ))}
      <button className="primary-btn" disabled={busy} data-testid="save-address-button">{busy ? "Saving…" : "Save address"}</button>
    </form>
  );
}

function OrderConfirmation({ order, isReview = false }) {
  const steps = [
    { key: "order_placed", label: "Order Placed", icon: Check, done: true },
    { key: "payment", label: isReview ? "Payment Review" : "Payment Done", icon: isReview ? Clock3 : BadgeCheck, done: !isReview, active: isReview },
    { key: "processing", label: "Processing", icon: Package, done: false },
    { key: "shipped", label: "Shipped", icon: Truck, done: false },
    { key: "delivered", label: "Delivered", icon: ShieldCheck, done: false },
  ];
  const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;
  return (
    <div className="order-confirmation" data-testid="order-success">
      {/* Header */}
      <div className="oc-header">
        <div className={`oc-icon${isReview ? " review" : ""}`}>
          {isReview ? <Clock3 size={32} /> : <Check size={32} />}
        </div>
        <div>
          <span className="eyebrow">{isReview ? "PAYMENT REVIEW" : "ORDER CONFIRMED"}</span>
          <h1>{isReview ? "Proof Submitted!" : "Thank You! Order Placed!"}</h1>
          <p>{isReview ? "Admin payment verify karenge aur order confirm kar denge." : "Aapka order confirm ho gaya! Abhi process hone wala hai."}</p>
        </div>
      </div>

      {/* Order Number + Delivery Estimate */}
      <div className="oc-top-row">
        <div className="oc-order-num">
          <span>Order Number</span>
          <strong data-testid="confirmed-order-number">#{order.order_number}</strong>
        </div>
        {!isReview && (
          <div className="oc-delivery-estimate" data-testid="order-delivery-estimate">
            <span>Estimated Delivery:</span>
            <b>{(() => { const d = new Date(); d.setDate(d.getDate() + 9); return d.toLocaleDateString("en-IN", { day: "numeric", month: "long" }); })()}</b>
          </div>
        )}
      </div>

      {/* Process Steps */}
      <div className="oc-steps" data-testid="order-process-steps">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={step.key} className={`oc-step${step.done ? " done" : step.active ? " active" : ""}`} data-testid={`oc-step-${step.key}`}>
              <div className="oc-step-icon"><Icon size={16} /></div>
              <span>{step.label}</span>
              {i < steps.length - 1 && <div className={`oc-step-line${step.done ? " done" : ""}`} />}
            </div>
          );
        })}
      </div>

      {/* Items */}
      <div className="oc-items" data-testid="confirmed-order-items">
        <h3>Items Ordered</h3>
        {order.items?.map((item) => (
          <div className="oc-item" key={item.product_id}>
            <img src={resolveMediaUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
            <div>
              <b>{item.name}</b>
              <small>Qty {item.quantity} {item.variant_sku ? `· ${item.variant_sku}` : ""}</small>
            </div>
            <strong>{money(item.price * item.quantity)}</strong>
          </div>
        ))}
      </div>

      {/* Summary + Address */}
      <div className="oc-meta-grid">
        <div className="oc-summary" data-testid="confirmed-order-summary">
          <h3>Payment Summary</h3>
          <div><span>Subtotal</span><b>{money(order.subtotal)}</b></div>
          {order.discount > 0 && <div><span>Discount</span><b className="green-text">−{money(order.discount)}</b></div>}
          <div><span>Platform fee</span><b>{money(order.platform_fee)}</b></div>
          <div className="oc-total"><span>Total</span><strong>{money(order.total)}</strong></div>
          <div className="oc-payment-method">
            <span>Payment via</span>
            <b>{order.payment?.method?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</b>
          </div>
          {isReview && <div className="oc-review-note"><Clock3 size={13} /> Admin verify karenge 2-4 ghante mein</div>}
        </div>

        <div className="oc-address" data-testid="confirmed-delivery-address">
          <h3>Delivery Address</h3>
          <p><b>{order.delivery_address?.recipient_name}</b></p>
          <p>{order.delivery_address?.line1}</p>
          <p>{order.delivery_address?.city} — {order.delivery_address?.postal_code}</p>
          <p>{order.delivery_address?.phone}</p>
          {!isReview && <div className="oc-delivery-badge" data-testid="oc-delivery-badge"><Truck size={12} /> Free Delivery · 7–10 business days</div>}
        </div>
      </div>

      {/* Actions */}
      <div className="oc-actions">
        <Link to="/my-orders" className="primary-btn" data-testid="oc-track-order-btn">Track Order</Link>
        <Link to="/" className="order-secondary-button" data-testid="oc-continue-shopping-btn">Continue Shopping</Link>
      </div>
    </div>
  );
}

function Checkout({ cart, user, refreshUser, refreshCart, common }) {
  const { Razorpay, isLoading: razorpayLoading, error: razorpayLoadError } = useRazorpay();
  const [paid, setPaid] = useState(false);
  const [method, setMethod] = useState("upi");
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [manualPayment, setManualPayment] = useState(null);
  const placeInFlight = useRef(false);
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total = Math.max(0, subtotal - (coupon?.discount || 0) + 99);
  const address = user?.addresses?.[0];
  const paymentMethods = useMemo(() => [
    ["upi", "UPI", "Pay using any UPI app via Razorpay"],
    ["bank_transfer", "Personal UPI QR", "Pay in GPay, PhonePe or Paytm + submit proof"],
    ["card", "Credit / Debit Card", "Visa, Mastercard, RuPay"],
    ["net_banking", "Net Banking", "All major banks"],
    ["wallet", "DMobileMart Wallet", "Use your available wallet balance"],
  ].filter(([value]) => paymentConfig?.methods?.[value] !== false), [paymentConfig]);
  useEffect(() => {
    Promise.all([api.get("/api/payment-config"), api.get("/api/content/coupons"), api.get("/api/wallet")]).then(([payment, coupons, wallet]) => { setPaymentConfig(payment.data); setAvailableCoupons(coupons.data); setWalletBalance(wallet.data.balance); }).catch(() => setPaymentConfig({ methods: { upi: true, bank_transfer: true, card: true, net_banking: true, wallet: true } }));
  }, []);
  useEffect(() => {
    if (paymentMethods.length && !paymentMethods.some(([value]) => value === method)) setMethod(paymentMethods[0][0]);
  }, [method, paymentMethods]);
  const applyCoupon = async (code = couponCode) => { const normalized = String(code ?? "").trim().toUpperCase(); if (!normalized) return; setCouponCode(normalized); setCouponBusy(true); try { const { data } = await api.post("/api/coupons/validate", { code: normalized, subtotal }); setCoupon(data); toast.success(`${data.code} applied`); } catch (error) { setCoupon(null); toast.error(apiError(error)); } finally { setCouponBusy(false); } };
  const markFailed = async (orderId, reason) => { try { await api.post("/api/payments/razorpay/fail", { order_id: orderId, reason }); } catch {} };
  const paymentSucceeded = useRef(false);
  const openRazorpay = async (order) => {
    if (razorpayLoadError || !Razorpay) throw new Error("Secure payment checkout could not load. Please try again.");
    const { data: paymentOrder } = await api.post("/api/payments/razorpay/order", { order_id: order.id });
    paymentSucceeded.current = false;
    const checkout = new Razorpay({
      key: paymentOrder.key_id, amount: paymentOrder.amount, currency: paymentOrder.currency, order_id: paymentOrder.razorpay_order_id, name: "DMobileMart", description: `Order ${paymentOrder.order_number}`,
      prefill: { name: paymentOrder.name, email: paymentOrder.email }, theme: { color: "#00d4ff" },
      method: { upi: method === "upi", card: method === "card", netbanking: method === "net_banking", wallet: false },
      handler: async (response) => { try { const { data } = await api.post("/api/payments/razorpay/verify", { order_id: order.id, razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature }); paymentSucceeded.current = true; setPaid(data); await refreshCart(); toast.success("Payment verified. Order confirmed!"); } catch (error) { await markFailed(order.id, "verification_failed"); toast.error(apiError(error)); } finally { setBusy(false); placeInFlight.current = false; } },
      modal: { ondismiss: async () => { if (!paymentSucceeded.current) { await markFailed(order.id, "checkout_dismissed_or_failed"); toast.error("Payment was not completed. Your order was not processed."); } setBusy(false); placeInFlight.current = false; } },
    });
    checkout.open();
  };
  const submitManualProof = async ({ reference, proof }) => { if (!manualPayment) return; setBusy(true); try { const payload = new FormData(); payload.append("file", proof); payload.append("payment_reference", reference); const { data } = await api.post(`/api/payments/manual-proof?order_id=${manualPayment.order_id}`, payload, { headers: { "Content-Type": "multipart/form-data" } }); setManualPayment(null); setReviewOrder(data); await refreshCart(); toast.success("UPI proof submitted for admin review"); } catch (error) { toast.error(apiError(error)); } finally { setBusy(false); } };
  const place = async () => {
    if (busy || placeInFlight.current || !cart.length || !paymentMethods.length) return;
    placeInFlight.current = true;
    if (!address) { placeInFlight.current = false; return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/orders", { address_id: address.id, payment_method: method, coupon_code: coupon?.code || null });
      if (method === "wallet") { setPaid(data); await refreshCart(); toast.success("Order placed successfully"); setBusy(false); placeInFlight.current = false; }
      else if (method === "bank_transfer") { try { const { data: manual } = await api.get(`/api/payments/manual-upi/${data.id}`); setManualPayment(manual); await refreshCart(); toast.success("Personal UPI QR ready"); } catch (error) { toast.error(apiError(error)); } finally { setBusy(false); placeInFlight.current = false; } }
      else { try { await openRazorpay(data); } catch (error) { await markFailed(data.id, "razorpay_order_creation_failed"); toast.error(apiError(error)); setBusy(false); placeInFlight.current = false; } }
    }
    catch (error) { toast.error(apiError(error)); setBusy(false); placeInFlight.current = false; }
  };
  return (
    <>
      <Topbar {...common} />
      <main className="checkout-page">
        <Link to="/cart" className="back-link" data-testid="checkout-back-link"><ArrowLeft size={16} /> Back to cart</Link>
        <div className="checkout-steps"><span className="done">1<br /><small>Address</small></span><i /><span className="active">2<br /><small>Payment</small></span><i /><span>3<br /><small>Place Order</small></span></div>
        {paid ? (
          <OrderConfirmation order={paid} />
        ) : reviewOrder ? (
          <OrderConfirmation order={reviewOrder} isReview />
        ) : manualPayment ? (
          <ManualUpiPayment payment={manualPayment} busy={busy} onSubmit={submitManualProof} onBack={() => setManualPayment(null)} />
        ) : !address ? <AddressForm onDone={refreshUser} /> : (
          <div className="checkout-grid">
            <section>
              <div className="checkout-section"><h3>Delivery address <Link to="/account" data-testid="change-checkout-address">Change</Link></h3><div className="address-card"><b>{address.label}</b><p>{address.recipient_name}</p><span>{address.line1}<br />{address.city} - {address.postal_code}<br />{address.phone}</span></div></div>
              <div className="checkout-section"><h3>Payment Options</h3>
                {paymentConfig?.partial_payment_enabled && method !== "wallet" && <p className="partial-payment-note" data-testid="partial-payment-note">आज सिर्फ़ {paymentConfig.partial_payment_percent}% advance दें — बाकी delivery से पहले।</p>}
                {method === "upi" && <div className="upi-checkout-note" data-testid="upi-checkout-note"><Smartphone size={16} /><span><b>UPI QR & app payment</b><small>Paytm, PhonePe, Google Pay या किसी भी installed UPI app को Razorpay secure checkout में चुनें।</small></span></div>}
                {method === "bank_transfer" && <div className="manual-transfer-card" data-testid="manual-transfer-details"><h4><Smartphone size={16} /> Personal UPI QR payment</h4><p>Generate secure QR, pay in your UPI app, then submit screenshot और UTR/reference number. Admin verify करने के बाद order confirm होगा.</p>{paymentConfig?.manual_transfer?.upi_id ? <div><span>Personal UPI ID</span><b data-testid="checkout-personal-upi-id">{paymentConfig.manual_transfer.upi_id}</b></div> : <div><span>UPI setup</span><b data-testid="checkout-personal-upi-missing">Currently unavailable</b></div>}</div>}
                {paymentMethods.map(([value, label, detail]) => (
                  <label className="payment-option" key={value}><input type="radio" name="payment" checked={method === value} onChange={() => setMethod(value)} data-testid={`payment-${value}`} /><span>{label}<small>{value === "wallet" && walletBalance !== null ? `Available balance: ${money(walletBalance)} · ${detail}` : detail}</small>{value === "wallet" && <Link to="/account#wallet" className="wallet-topup-link" data-testid="wallet-add-money-link">Add money</Link>}</span><ChevronRight size={15} /></label>
                ))}
                {!paymentMethods.length && <p className="payment-unavailable" data-testid="payment-methods-unavailable">अभी कोई payment method उपलब्ध नहीं है। कृपया थोड़ी देर बाद कोशिश करें।</p>}
              </div>
            </section>
            <aside className="summary">
              <h3>Payment Summary</h3>
              <div><span>Items total</span><b>{money(subtotal)}</b></div>
              <div className="coupon-control"><input value={couponCode} onChange={(event) => setCouponCode(event.target.value.toUpperCase())} placeholder="Coupon code" data-testid="checkout-coupon-input" /><button type="button" onClick={() => applyCoupon()} disabled={couponBusy || !cart.length} data-testid="apply-coupon-button">{couponBusy ? "Checking…" : "Apply"}</button></div>
              {availableCoupons.length > 0 && <div className="coupon-pills" data-testid="available-coupon-pills">{availableCoupons.map((item) => <button type="button" key={item.id} onClick={() => applyCoupon(item.data?.code || item.title)} data-testid={`coupon-pill-${item.id}`}><b>{item.data?.code || item.title}</b><small>{item.description || "Tap to apply"}</small></button>)}</div>}
              {coupon && <div className="coupon-result" data-testid="coupon-result"><span>{coupon.code} discount</span><b className="green-text">−{money(coupon.discount)}</b></div>}
              <div><span>Platform fee</span><b>₹99</b></div>
              <div><span>Delivery</span><b className="green-text">FREE</b></div>
              <hr />
              <div className="total"><span>Payable Now</span><strong>{money(total)}</strong></div>
              <button onClick={place} disabled={busy || placeInFlight.current || !cart.length || !paymentMethods.length} className="primary-btn full" data-testid="place-order-button">{busy ? "Placing order…" : method === "bank_transfer" ? "Generate personal UPI QR" : `Pay ${money(total)} Now`}</button>
              {razorpayLoading && !busy && <p className="rzp-loading-note" data-testid="razorpay-loading-note"><LoaderCircle size={12} className="spin" /> Loading payment gateway…</p>}
              <small className="secure"><ShieldCheck size={12} /> 100% secure payment</small>
            </aside>
          </div>
        )}
      </main>
      <FooterBar />
    </>
  );
}

function Auctions({ auctions, user, refreshAuctions, common }) {
  return <AuctionExperience auctions={auctions} user={user} refreshAuctions={refreshAuctions} common={common} Topbar={Topbar} FooterBar={FooterBar} BottomNav={BottomNav} Loading={Loading} />;
}

function SellPage({ common }) {
  return (
    <>
      <Topbar {...common} />
      <main className="simple-page">
        <div className="page-heading"><div><span className="eyebrow">SELL ON MOBILECART</span><h1>Sell Your Device</h1><p>Turn your old gadgets into instant cash.</p></div></div>
        <div className="sell-grid">
          {[[Smartphone, "Get Instant Quote", "Get your device value in seconds"], [Truck, "Free Pickup", "Doorstep pickup across India"], [Wallet, "Instant Payment", "Money in your wallet same day"]].map(([Icon, title, sub]) => (
            <div className="sell-card" key={title}><span><Icon size={26} /></span><b>{title}</b><small>{sub}</small></div>
          ))}
        </div>
        <div className="sell-cta"><b>Ready to sell?</b><Link to="/account" className="primary-btn" data-testid="sell-start-button">Start Selling <ChevronRight size={15} /></Link></div>
      </main>
      <FooterBar />
      <BottomNav {...common} active="" />
    </>
  );
}

function Login({ setUser }) {
  const navigate = useNavigate(); const location = useLocation();
  const isAdminLogin = new URLSearchParams(location.search).get("next")?.startsWith("/admin");
  const nextPath = new URLSearchParams(location.search).get("next");

  // ── Admin-only login (hidden, accessed via /login?next=/admin) ──
  const [adminForm, setAdminForm] = useState({ identifier: "", password: "" });
  const [adminBusy, setAdminBusy] = useState(false);
  const submitAdmin = async (event) => {
    event.preventDefault(); setAdminBusy(true);
    try {
      const { data } = await api.post("/api/auth/login", { identifier: adminForm.identifier, password: adminForm.password });
      setUser(data); toast.success("Admin login successful");
      navigate(nextPath?.startsWith("/admin") ? nextPath : "/admin");
    } catch (error) { toast.error(apiError(error)); } finally { setAdminBusy(false); }
  };

  if (isAdminLogin) {
    return (
      <main className="auth-page">
        <Link to="/" data-testid="login-brand-link"><Brand /></Link>
        <form className="auth-card" onSubmit={submitAdmin} data-testid="auth-form">
          <span className="eyebrow">ADMIN ACCESS</span>
          <h1>Admin Sign In</h1>
          <input type="text" value={adminForm.identifier} onChange={(e) => setAdminForm({ ...adminForm, identifier: e.target.value })} placeholder="Username / email" required data-testid="auth-email-input" autoComplete="username" />
          <input type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Password" minLength="8" required data-testid="auth-password-input" autoComplete="current-password" />
          <button className="primary-btn full" disabled={adminBusy} data-testid="auth-submit-button">{adminBusy ? "Signing in…" : "Sign in"}</button>
        </form>
      </main>
    );
  }

  // ── Customer login: OTP + Email tabs ──
  return <CustomerLogin setUser={setUser} nextPath={nextPath} />;
}

function CustomerLogin({ setUser, nextPath }) {
  const navigate = useNavigate();
  const refCode = new URLSearchParams(window.location.search).get("ref") || "";
  const [tab, setTab] = useState("otp"); // "otp" | "email"

  // ── OTP flow state ──
  const [step, setStep] = useState("phone"); // phone | otp | name
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [referralInput, setReferralInput] = useState(refCode);
  const [devOtp, setDevOtp] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // ── Email flow state ──
  const [emailMode, setEmailMode] = useState("login"); // login | register
  const [emailForm, setEmailForm] = useState({ name: "", email: "", password: "", confirmPassword: "", referral_code: refCode });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const sendOtp = async (event) => {
    event?.preventDefault(); if (otpBusy) return;
    const cleaned = phone.replace(/\D/g, "");
    if (!/^[6-9][0-9]{9}$/.test(cleaned)) { toast.error("10-digit valid mobile number daalo"); return; }
    setOtpBusy(true);
    try {
      const { data } = await api.post("/api/auth/send-otp", { phone: cleaned });
      setDevOtp(data.dev_otp || "");
      setResendTimer(30);
      setStep("otp");
      toast.success("OTP bheja gaya!");
    } catch (error) { toast.error(apiError(error)); } finally { setOtpBusy(false); }
  };

  const verifyOtp = async (event) => {
    event?.preventDefault(); if (otpBusy) return;
    const cleaned = phone.replace(/\D/g, "");
    if (otp.length !== 6) { toast.error("6-digit OTP daalo"); return; }
    setOtpBusy(true);
    try {
      const { data } = await api.post("/api/auth/verify-otp", { phone: cleaned, otp, name: name || undefined, referral_code: referralInput.trim().toUpperCase() || undefined });
      setUser(data); toast.success("Login successful! Swagat hai!");
      navigate(nextPath || "/");
    } catch (error) {
      const msg = apiError(error);
      if (msg.includes("Name is required")) { setStep("name"); }
      else { toast.error(msg); }
    } finally { setOtpBusy(false); }
  };

  const submitEmail = async (event) => {
    event.preventDefault(); setEmailError(""); setEmailBusy(true);
    try {
      if (emailMode === "register" && emailForm.password !== emailForm.confirmPassword) throw new Error("Passwords do not match");
      const endpoint = emailMode === "register" ? "/api/auth/register" : "/api/auth/login";
      const refCodeVal = (emailForm.referral_code || "").trim().toUpperCase() || undefined;
      const payload = emailMode === "register"
        ? { name: emailForm.name, email: emailForm.email, password: emailForm.password, confirm_password: emailForm.confirmPassword, referral_code: refCodeVal }
        : { identifier: emailForm.email, password: emailForm.password };
      const { data } = await api.post(endpoint, payload);
      setUser(data); toast.success(emailMode === "register" ? "Account bana liya!" : "Welcome back!");
      navigate(data.role === "admin" ? "/admin" : nextPath || "/");
    } catch (error) {
      const msg = error.message === "Passwords do not match" ? error.message : apiError(error);
      setEmailError(msg); toast.error(msg);
    } finally { setEmailBusy(false); }
  };

  return (
    <main className="auth-page">
      <Link to="/" data-testid="login-brand-link"><Brand /></Link>
      <div className="auth-card otp-card" data-testid="auth-form">
        <span className="eyebrow">DMMOBILE ACCOUNT</span>

        {/* Login method tabs */}
        <div className="login-tabs" data-testid="login-tabs">
          <button type="button" className={tab === "otp" ? "active" : ""} onClick={() => setTab("otp")} data-testid="tab-otp">Mobile OTP</button>
          <button type="button" className={tab === "email" ? "active" : ""} onClick={() => setTab("email")} data-testid="tab-email">Email</button>
        </div>

        {/* ── OTP Tab ── */}
        {tab === "otp" && step === "phone" && (
          <form onSubmit={sendOtp}>
            <h1>Mobile se login karo</h1>
            <p className="auth-help">OTP aapke mobile number pe aayega</p>
            <div className="phone-input-row">
              <span className="phone-prefix">+91</span>
              <input type="tel" inputMode="numeric" maxLength="10" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile number" required data-testid="phone-input" autoComplete="tel" />
            </div>
            <button className="primary-btn full" disabled={otpBusy || phone.length < 10} data-testid="send-otp-button">{otpBusy ? "Bhej raha hoon…" : "OTP Bhejo"}</button>
          </form>
        )}

        {tab === "otp" && step === "otp" && (
          <form onSubmit={verifyOtp}>
            <h1>OTP verify karo</h1>
            <p className="auth-help">+91 {phone} pe bheja gaya &nbsp;
              <button type="button" className="phone-change-btn" onClick={() => { setStep("phone"); setOtp(""); setDevOtp(""); }} data-testid="change-phone-btn">badlo</button>
            </p>
            {devOtp && <div className="dev-otp-box" data-testid="dev-otp-display">Dev OTP: <strong>{devOtp}</strong><small>(Fast2SMS key lagane pe band ho jayega)</small></div>}
            <input type="text" inputMode="numeric" maxLength="6" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" required data-testid="otp-input" autoComplete="one-time-code" className="otp-input" />
            <button className="primary-btn full" disabled={otpBusy || otp.length < 6} data-testid="verify-otp-button">{otpBusy ? "Verify ho raha hai…" : "Verify karo"}</button>
            <button type="button" className="auth-switch" disabled={resendTimer > 0} onClick={() => { setOtp(""); setDevOtp(""); sendOtp(); }} data-testid="resend-otp-button">{resendTimer > 0 ? `Resend in ${resendTimer}s` : "OTP dobara bhejo"}</button>
          </form>
        )}

        {tab === "otp" && step === "name" && (
          <form onSubmit={verifyOtp}>
            <h1>Swagat hai!</h1>
            <p className="auth-help">Pehli baar ho? Bas apna naam batao</p>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Aapka poora naam" required minLength="2" data-testid="name-input" autoComplete="name" />
            <input value={referralInput} onChange={(e) => setReferralInput(e.target.value.toUpperCase())} placeholder="Referral code (optional) — ₹50 bonus!" maxLength="24" data-testid="referral-code-input" style={{ textTransform: "uppercase", letterSpacing: "1px" }} />
            <button className="primary-btn full" disabled={otpBusy} data-testid="confirm-name-button">{otpBusy ? "Account ban raha hai…" : "Account banao"}</button>
          </form>
        )}

        {/* ── Email Tab ── */}
        {tab === "email" && (
          <form onSubmit={submitEmail}>
            <h1>{emailMode === "register" ? "Account banao" : "Email se login"}</h1>
            {emailMode === "register" && (
              <input value={emailForm.name} onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })} placeholder="Poora naam" required data-testid="register-name-input" />
            )}
            <input type="email" value={emailForm.email} onChange={(e) => { setEmailForm({ ...emailForm, email: e.target.value }); setEmailError(""); }} placeholder="Email address" required data-testid="auth-email-input" autoComplete="username" />
            <input type="password" value={emailForm.password} onChange={(e) => { setEmailForm({ ...emailForm, password: e.target.value }); setEmailError(""); }} placeholder="Password" minLength="8" required data-testid="auth-password-input" autoComplete={emailMode === "register" ? "new-password" : "current-password"} />
            {emailMode === "register" && (
              <>
                <input type="password" value={emailForm.confirmPassword} onChange={(e) => setEmailForm({ ...emailForm, confirmPassword: e.target.value })} placeholder="Password dobara daalo" minLength="8" required data-testid="register-confirm-password-input" autoComplete="new-password" />
                <input value={emailForm.referral_code} onChange={(e) => setEmailForm({ ...emailForm, referral_code: e.target.value.toUpperCase() })} placeholder="Referral code (optional) — ₹50 bonus!" maxLength="24" data-testid="referral-code-input" style={{ textTransform: "uppercase", letterSpacing: "1px" }} />
              </>
            )}
            {emailError && <p className="auth-error" role="alert" data-testid="auth-error-message">{emailError}</p>}
            <button className="primary-btn full" disabled={emailBusy} data-testid="auth-submit-button">{emailBusy ? "Please wait…" : emailMode === "register" ? "Account banao" : "Login karo"}</button>
            <button type="button" className="auth-switch" onClick={() => { setEmailMode(emailMode === "register" ? "login" : "register"); setEmailError(""); }} data-testid="auth-switch-button">
              {emailMode === "register" ? "Pehle se account hai? Login karo" : "Naya account banao"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

function ThemeControl({ theme, onThemeChange }) {
  return (
    <section className="theme-control" data-testid="profile-theme-control">
      <div className="theme-control-copy">
        <span className="theme-icon">{theme === "dark" ? <Moon size={17} /> : <Sun size={17} />}</span>
        <div>
          <b data-testid="theme-mode-label">{theme === "dark" ? "Dark Mode" : "Light Mode"}</b>
          <small>{theme === "dark" ? "Switch to light for better readability" : "Light mode active — bright & clear"}</small>
        </div>
      </div>
      <button className={`theme-toggle-btn ${theme}`} onClick={onThemeChange} data-testid="theme-toggle-button" aria-label="Toggle theme">
        <span className="toggle-pill" />
      </button>
    </section>
  );
}

/* ── Sell Requests List (Account page) ── */
function SellRequestsList({ requests, onRefresh, onSelect }) {
  const STATUS_LABEL = {
    pending:         { text: "Under Review",     color: "#f59e0b" },
    under_review:    { text: "Under Review",     color: "#f59e0b" },
    offer_made:      { text: "Offer Received",   color: "#00d4ff" },
    accepted:        { text: "Offer Accepted",   color: "#22c55e" },
    in_transit:      { text: "In Transit",       color: "#a78bfa" },
    store_visit:     { text: "Store Visit",      color: "#a78bfa" },
    received:        { text: "Device Received",  color: "#22c55e" },
    qc_done:         { text: "QC Done",          color: "#00d4ff" },
    final_accepted:  { text: "Confirm Docs",     color: "#22c55e" },
    docs_submitted:  { text: "Docs Submitted",   color: "#a78bfa" },
    payment_processing:{ text: "Payment Processing", color: "#f59e0b" },
    paid:            { text: "Paid!",            color: "#22c55e" },
    rejected:        { text: "Rejected",         color: "#ef4444" },
    cancelled:       { text: "Cancelled",        color: "#ef4444" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="sell-requests-list">
      {requests.map(req => {
        const sl = STATUS_LABEL[req.status] || { text: req.status, color: "#8fa5c4" };
        const needsAction = ["offer_made","final_accepted","accepted","qc_done"].includes(req.status);
        return (
          <div key={req.id} data-testid={`sell-request-${req.id}`}
            style={{ background: "rgba(0,212,255,0.04)", border: `1px solid ${needsAction ? "rgba(0,212,255,0.25)" : "rgba(0,212,255,0.1)"}`, borderRadius: 12, padding: "14px 16px", cursor: "pointer" }}
            onClick={() => onSelect && onSelect(req.id)}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div>
                <b style={{ color: "#e2eafc", fontSize: 13 }}>{req.brand} {req.model} · {req.storage}</b>
                <small style={{ color: "#6b7f9e", display: "block", fontSize: 11 }}>{req.condition} · {new Date(req.created_at || Date.now()).toLocaleDateString("en-IN")} · #{req.request_number}</small>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: sl.color, background: `${sl.color}18`, padding: "3px 10px", borderRadius: 20, border: `1px solid ${sl.color}35` }}>
                  {sl.text}
                </span>
                {needsAction && <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>Action Required</span>}
                <ChevronRight size={14} style={{ color: "#3e5a7a" }} />
              </div>
            </div>
            {req.status === "offer_made" && req.offer_price && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#8fa5c4" }}>
                Offer: <strong style={{ color: "#00d4ff" }}>₹{Number(req.offer_price).toLocaleString("en-IN")}</strong> — click to respond
              </div>
            )}
            {req.status === "qc_done" && req.final_price && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#8fa5c4" }}>
                Final QC Offer: <strong style={{ color: "#22c55e" }}>₹{Number(req.final_price).toLocaleString("en-IN")}</strong> — click to confirm
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Account({ user, setUser, common, theme, onThemeChange, refreshUser, onAdd, refreshWish }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [sellRequests, setSellRequests] = useState([]);
  const [sellLoading, setSellLoading] = useState(true);
  const [selectedSellId, setSelectedSellId] = useState(null);
  const [referral, setReferral] = useState(null);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const [profileBusy, setProfileBusy] = useState(false);

  // Address editing state
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState({});
  const [addrBusy, setAddrBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/api/orders?page_size=50"), api.get("/api/wallet")])
      .then(([o, w]) => { setOrders(o.data.items); setWallet(w.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/api/sell-requests")
      .then(({ data }) => setSellRequests(data || []))
      .catch(() => {})
      .finally(() => setSellLoading(false));
    api.get("/api/auth/me/referral")
      .then(({ data }) => setReferral(data))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const hash = location.hash?.replace("#", "");
    if (hash) { setActiveSection(hash); setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200); }
  }, [location.hash]);

  if (!user) return <Navigate to="/login?next=/account" replace />;

  const activeOrders = orders.filter(o => !["delivered", "cancelled"].includes(o.status)).length;
  const pendingPayment = orders.filter(o => ["payment_failed", "payment_pending"].includes(o.status)).length;
  const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;

  const startEditProfile = () => {
    setProfileForm({ name: user.name || "", phone: user.phone || "" });
    setEditingProfile(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileBusy(true);
    try {
      const { data } = await api.patch("/api/auth/me", { name: profileForm.name, phone: profileForm.phone || null });
      setUser(data);
      setEditingProfile(false);
      toast.success("Profile update ho gaya!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setProfileBusy(false);
    }
  };

  const startEditAddress = (addr) => {
    setEditingAddressId(addr.id);
    setAddressForm({ label: addr.label || "HOME", recipient_name: addr.recipient_name || "", phone: addr.phone || "", line1: addr.line1 || "", line2: addr.line2 || "", city: addr.city || "", state: addr.state || "", postal_code: addr.postal_code || "", country: addr.country || "India" });
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    setAddrBusy(true);
    try {
      await api.patch(`/api/auth/me/addresses/${editingAddressId}`, addressForm);
      await refreshUser();
      setEditingAddressId(null);
      toast.success("Address update ho gaya!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAddrBusy(false);
    }
  };

  const deleteAddress = async (addrId) => {
    if (!window.confirm("Yeh address delete karein?")) return;
    try {
      await api.delete(`/api/auth/me/addresses/${addrId}`);
      await refreshUser();
      toast.success("Address delete ho gaya!");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Quick actions
  const quickActions = [
    { icon: Package, label: "Orders", sub: `${activeOrders} active`, to: "/my-orders", badge: pendingPayment, testid: "profile-orders" },
    { icon: ShoppingCart, label: "Cart", sub: `${common.cartCount} items`, to: "/cart", testid: "profile-cart" },
    { icon: Wallet, label: "Wallet", sub: money(wallet?.balance || 0), to: "#wallet", testid: "profile-wallet" },
    { icon: Gavel, label: "Auctions", sub: "Live bids", to: "/auctions", testid: "profile-auctions" },
    { icon: MapPin, label: "Addresses", sub: `${user.addresses?.length || 0} saved`, to: "#addresses", testid: "profile-addresses" },
    { icon: RotateCcw, label: "Returns", sub: "Warranty claims", to: "#returns", testid: "profile-returns" },
  ];

  return (
    <>
      <Topbar {...common} />
      <main className="profile-page" data-testid="account-page">
        <div className="profile-layout-grid">

          {/* ── LEFT SIDEBAR (desktop) ── */}
          <aside className="profile-sidebar">

        {/* ── Profile Header ── */}
        <div className="profile-header" data-testid="profile-header">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar" data-testid="account-avatar">{user.name.slice(0, 1).toUpperCase()}</div>
            <div className="profile-avatar-ring" />
          </div>
          <div className="profile-identity">
            {editingProfile ? (
              <form onSubmit={saveProfile} style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="profile-edit-form">
                <input value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} placeholder="Full name" required minLength={2} maxLength={100} data-testid="profile-name-input" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #1e3050", background: "var(--card-bg, #0d1f35)", color: "var(--text-primary, #e0e8f0)", fontSize: 14, width: "100%" }} />
                <input value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} placeholder="Phone (optional)" maxLength={20} data-testid="profile-phone-input" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #1e3050", background: "var(--card-bg, #0d1f35)", color: "var(--text-primary, #e0e8f0)", fontSize: 14, width: "100%" }} />
                <div style={{ display: "flex", gap: 6 }}>
                  <button type="submit" disabled={profileBusy} data-testid="save-profile-button" style={{ flex: 1, padding: "6px 12px", borderRadius: 8, background: "#00d4ff22", border: "1px solid #00d4ff44", color: "#00d4ff", cursor: "pointer", fontSize: 12 }}>
                    <Save size={12} style={{ marginRight: 4 }} />{profileBusy ? "Saving…" : "Save"}
                  </button>
                  <button type="button" onClick={() => setEditingProfile(false)} data-testid="cancel-profile-edit" style={{ padding: "6px 12px", borderRadius: 8, background: "transparent", border: "1px solid #3a5070", color: "#8899b0", cursor: "pointer", fontSize: 12 }}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <h2 data-testid="account-user-name">{user.name}</h2>
                <p data-testid="account-user-email">{user.phone ? `+91 ${user.phone}` : user.email?.includes("@mobilecart.app") ? "" : user.email}</p>
                <span className="profile-gold-badge"><BadgeCheck size={12} /> DM Gold Member</span>
                <button onClick={startEditProfile} data-testid="edit-profile-button" style={{ marginTop: 6, padding: "4px 10px", borderRadius: 6, background: "transparent", border: "1px solid #1e3050", color: "#8899b0", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                  <Pencil size={11} /> Edit Profile
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div className="profile-stats-row" data-testid="profile-stats">
          <div className="profile-stat" onClick={() => navigate("/my-orders")} data-testid="stat-orders">
            <strong>{orders.length}</strong><span>Orders</span>
            {pendingPayment > 0 && <i className="stat-alert">{pendingPayment} pending</i>}
          </div>
          <div className="profile-stat-divider" />
          <div className="profile-stat" onClick={() => { setActiveSection("wallet"); document.getElementById("wallet")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="stat-wallet">
            <strong>{money(wallet?.balance || 0)}</strong><span>Wallet</span>
          </div>
          <div className="profile-stat-divider" />
          <div className="profile-stat" onClick={() => navigate("/auctions")} data-testid="stat-auctions">
            <strong>{common.cartCount}</strong><span>In Cart</span>
          </div>
        </div>

        {/* ── Quick Actions Grid ── */}
        <div className="profile-section-card">
          <div className="profile-quick-grid" data-testid="profile-quick-actions">
            {quickActions.map(({ icon: Icon, label, sub, to, badge, testid }) => (
              <Link to={to} key={label} className="profile-quick-item" data-testid={testid}>
                <div className="pqi-icon"><Icon size={22} />{badge > 0 && <span className="pqi-badge">{badge}</span>}</div>
                <b>{label}</b>
                <small>{sub}</small>
              </Link>
            ))}
          </div>
        </div>

          </aside>

          {/* ── RIGHT MAIN CONTENT ── */}
          <div className="profile-main-content">

        {/* ── Wallet Section ── */}
        <div className="profile-section-card" id="wallet" data-testid="customer-wallet-card">
          <div className="profile-section-head">
            <Wallet size={18} /><h3>My Wallet</h3>
            <Link to="/checkout" className="profile-section-action" data-testid="wallet-use-link">Use at Checkout</Link>
          </div>
          <div className="wallet-balance-display">
            <div>
              <span className="wallet-bal-label">Available Balance</span>
              <strong className="wallet-bal-amount" data-testid="customer-wallet-balance">{money(wallet?.balance || 0)}</strong>
            </div>
            <div className="wallet-neon-orb" />
          </div>
          <div className="wallet-txn-list" data-testid="customer-wallet-history">
            {loading ? <p className="profile-muted">Loading…</p> :
              wallet?.transactions?.length ? wallet.transactions.slice(0, 5).map((txn) => (
                <div className="wallet-txn-row" key={txn.id} data-testid={`customer-wallet-transaction-${txn.id}`}>
                  <div className={`txn-dot ${txn.kind}`} />
                  <div className="txn-info"><b>{txn.note}</b><small>{new Date(txn.created_at).toLocaleDateString("en-IN")}</small></div>
                  <span className={`txn-amount ${txn.kind}`}>{txn.kind === "credit" ? "+" : "−"}{money(txn.amount)}</span>
                </div>
              )) : <p className="profile-muted" data-testid="customer-wallet-empty-state">Koi transactions nahi hain abhi.</p>
            }
          </div>
        </div>

        {/* ── Addresses ── */}
        <div className="profile-section-card" id="addresses" data-testid="account-addresses-section">
          <div className="profile-section-head"><MapPin size={18} /><h3>Delivery Addresses</h3></div>
          {user.addresses?.length ? (
            <div className="profile-address-list">
              {user.addresses.map((addr) => (
                <div key={addr.id}>
                  {editingAddressId === addr.id ? (
                    <form onSubmit={saveAddress} className="profile-address-card" style={{ gap: 8, flexDirection: "column" }} data-testid={`edit-address-form-${addr.id}`}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                        {[["label", "Label (HOME/WORK)"], ["recipient_name", "Full Name"], ["phone", "Phone"], ["line1", "Address Line"], ["city", "City"], ["state", "State"], ["postal_code", "PIN Code"]].map(([key, ph]) => (
                          <input key={key} value={addressForm[key] || ""} onChange={e => setAddressForm({ ...addressForm, [key]: e.target.value })} placeholder={ph} required={key !== "label"} data-testid={`edit-addr-${key}`} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #1e3050", background: "var(--card-bg, #0d1f35)", color: "var(--text-primary, #e0e8f0)", fontSize: 12, gridColumn: key === "line1" ? "span 2" : "span 1" }} />
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="submit" disabled={addrBusy} data-testid={`save-address-edit-${addr.id}`} style={{ flex: 1, padding: "6px 12px", borderRadius: 8, background: "#00d4ff22", border: "1px solid #00d4ff44", color: "#00d4ff", cursor: "pointer", fontSize: 12 }}>{addrBusy ? "Saving…" : "Save Address"}</button>
                        <button type="button" onClick={() => setEditingAddressId(null)} style={{ padding: "6px 12px", borderRadius: 8, background: "transparent", border: "1px solid #3a5070", color: "#8899b0", cursor: "pointer", fontSize: 12 }}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div className="profile-address-card" data-testid={`saved-address-${addr.id}`}>
                      <div className="addr-icon"><MapPin size={15} /></div>
                      <div style={{ flex: 1 }}>
                        <b>{addr.label || "Home"}</b>
                        <p>{addr.recipient_name}</p>
                        <small>{addr.line1}, {addr.city} — {addr.postal_code}</small>
                        {addr.phone && <small>{addr.phone}</small>}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginLeft: 8 }}>
                        <button onClick={() => startEditAddress(addr)} data-testid={`edit-address-${addr.id}`} style={{ padding: "4px 8px", borderRadius: 6, background: "#00d4ff11", border: "1px solid #00d4ff33", color: "#00d4ff", cursor: "pointer", fontSize: 11 }}><Pencil size={11} /></button>
                        <button onClick={() => deleteAddress(addr.id)} data-testid={`delete-address-${addr.id}`} style={{ padding: "4px 8px", borderRadius: 6, background: "#ef444411", border: "1px solid #ef444433", color: "#ef4444", cursor: "pointer", fontSize: 11 }}><Trash2 size={11} /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="profile-muted" data-testid="account-no-addresses">Checkout ke waqt address add hoga.</p>
          )}
        </div>

        {/* ── Returns & Warranty ── */}
        <div className="profile-section-card" id="returns" data-testid="account-returns-section">
          <div className="profile-section-head"><RotateCcw size={18} /><h3>Returns & Warranty</h3></div>
          <AccountReturns orders={orders} loading={loading} />
        </div>

        {/* ── Recent Orders Mini ── */}
        <div className="profile-section-card" data-testid="profile-recent-orders">
          <div className="profile-section-head"><Package size={18} /><h3>Recent Orders</h3><Link to="/my-orders" className="profile-section-action" data-testid="view-all-orders-link">View all</Link></div>
          {loading ? <p className="profile-muted">Loading…</p> : orders.length ? (
            <div className="profile-orders-mini">
              {orders.slice(0, 3).map((order) => (
                <Link to={`/my-orders/${order.id}`} className="profile-order-row" key={order.id} data-testid={`profile-order-${order.id}`}>
                  <img src={resolveMediaUrl(order.items?.[0]?.image) || PLACEHOLDER_IMAGE} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                  <div>
                    <b>{order.items?.[0]?.name}{order.items?.length > 1 ? ` +${order.items.length - 1}` : ""}</b>
                    <small>{order.order_number} · {money(order.total)}</small>
                  </div>
                  <span className={`mini-status status-${order.status}`}>{order.status.replace(/_/g, " ")}</span>
                </Link>
              ))}
            </div>
          ) : <p className="profile-muted">Koi orders nahi hain abhi. <Link to="/category">Shopping start karo</Link></p>}
        </div>

        {/* ── Sell Requests ── */}
        <div className="profile-section-card" id="sell-requests" data-testid="account-sell-requests-section">
          <div className="profile-section-head">
            <Smartphone size={18} /><h3>Meri Sell Requests</h3>
            <Link to="/sell" className="profile-section-action" data-testid="new-sell-request-link">+ New Request</Link>
          </div>
          {selectedSellId ? (
            <SellRequestFlow
              requestId={selectedSellId}
              onBack={() => { setSelectedSellId(null); api.get("/api/sell-requests").then(({ data }) => setSellRequests(data || [])).catch(() => {}); }}
            />
          ) : sellLoading ? <p className="profile-muted">Loading…</p>
            : sellRequests.length === 0
              ? <p className="profile-muted" data-testid="sell-requests-empty">Koi sell request nahi hai. <Link to="/sell">iPhone becho →</Link></p>
              : <SellRequestsList requests={sellRequests} onRefresh={() => api.get("/api/sell-requests").then(({ data }) => setSellRequests(data || [])).catch(() => {})} onSelect={setSelectedSellId} />
          }
        </div>

        {/* ── Refer & Earn ── */}
        <div className="profile-section-card refer-earn-card" id="referral" data-testid="referral-section">
          <div className="profile-section-head"><Gift size={18} /><h3>Refer & Earn</h3></div>
          <div className="refer-earn-body">
            <div className="refer-earn-hero">
              <div className="refer-earn-reward">
                <span className="refer-earn-rupee">₹50</span>
                <span className="refer-earn-label">Referrer Gets</span>
              </div>
              <div className="refer-earn-divider">+</div>
              <div className="refer-earn-reward">
                <span className="refer-earn-rupee">₹50</span>
                <span className="refer-earn-label">New User Gets</span>
              </div>
            </div>
            <p className="refer-earn-desc">Apna code share karo — jab koi naya user sign up kare aur shopping kare, <b>dono ko ₹50 wallet credit milega!</b></p>
            {referral ? (
              <>
                <div className="refer-code-box" data-testid="referral-code-display">
                  <span className="refer-code-text">{referral.referral_code}</span>
                  <button className="refer-copy-btn" data-testid="copy-referral-code" onClick={() => { navigator.clipboard.writeText(referral.referral_code); toast.success("Referral code copy ho gaya!"); }}>
                    <Copy size={14} /> Copy
                  </button>
                </div>
                <div className="refer-share-row">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`DMMobile Shop se best deal pe iPhones kharido! Mera referral code use karo: ${referral.referral_code} aur signup pe ₹50 pao. ${window.location.origin}/register?ref=${referral.referral_code}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="refer-whatsapp-btn" data-testid="whatsapp-share-btn"
                  >
                    <MessageCircle size={15} /> Share on WhatsApp
                  </a>
                  <button className="refer-share-btn" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?ref=${referral.referral_code}`); toast.success("Referral link copy ho gaya!"); }} data-testid="copy-referral-link">
                    <Link2 size={14} /> Copy Link
                  </button>
                </div>
                <div className="refer-stats-row" data-testid="referral-stats">
                  <div className="refer-stat"><b>{referral.total_referrals}</b><span>Referrals</span></div>
                  <div className="refer-stat"><b>₹{referral.total_earned}</b><span>Earned</span></div>
                </div>
              </>
            ) : (
              <p className="profile-muted">Loading referral info…</p>
            )}
          </div>
        </div>

        {/* ── Sign Out ── */}
        <div className="profile-signout-wrap">
          <ThemeControl theme={theme} onThemeChange={onThemeChange} />
          <button className="profile-signout-btn" onClick={async () => { await api.post("/api/auth/logout"); setUser(null); }} data-testid="account-signout-button">
            <LogOut size={17} /> Sign Out
          </button>
        </div>

          </div>{/* end profile-main-content */}
        </div>{/* end profile-layout-grid */}

      </main>
      <FooterBar />
      <BottomNav {...common} active="Account" />
    </>
  );
}

function AccountReturns({ orders, loading }) {
  const [draft, setDraft] = useState(null);
  const [returns, setReturns] = useState([]);
  const [reason, setReason] = useState("defective");
  const [detail, setDetail] = useState("");
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/returns").then(({ data }) => setReturns(data || [])).catch(() => {});
  }, []);

  const warrantyDays = (order) => {
    const item = order.items?.[0];
    if (!item?.warranty_days || !["confirmed", "packed", "shipped", "delivered"].includes(order.status)) return 0;
    const deliveredAt = new Date(order.updated_at || order.created_at);
    const expiry = new Date(deliveredAt.getTime() + item.warranty_days * 86400000);
    return Math.max(0, Math.ceil((expiry - Date.now()) / 86400000));
  };

  const submitReturn = async (event) => {
    event.preventDefault(); setBusy(true);
    try {
      const fd = new FormData(); fd.append("order_id", draft.id); fd.append("reason", reason); fd.append("detail", detail);
      photos.forEach((p) => fd.append("photos", p));
      if (video) fd.append("video", video);
      await api.post("/api/returns", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Return request submitted!"); setDraft(null); setDetail(""); setPhotos([]); setVideo(null);
      const { data } = await api.get("/api/returns"); setReturns(data || []);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const deliveredOrders = orders.filter(o => ["confirmed", "packed", "shipped", "delivered"].includes(o.status));

  if (loading) return <p className="profile-muted">Loading…</p>;
  if (!deliveredOrders.length) return <p className="profile-muted">Koi active orders nahi hain warranty ke liye.</p>;

  return (
    <div className="returns-list" data-testid="returns-list">
      {deliveredOrders.map((order) => {
        const days = warrantyDays(order);
        const claim = returns.find(r => r.data?.order_id === order.id);
        return (
          <div className="return-row" key={order.id} data-testid={`return-row-${order.id}`}>
            <div className="return-row-info">
              <b>{order.order_number}</b>
              <small>{order.items?.[0]?.name}</small>
              {days > 0 ? <span className="warranty-badge active">{days} days warranty left</span> : <span className="warranty-badge expired">Warranty expired</span>}
            </div>
            {claim ? (
              <span className="return-status" data-testid={`return-requested-${order.id}`}>{claim.status.replace(/_/g, " ")}</span>
            ) : days > 0 ? (
              <button className="return-claim-btn" onClick={() => setDraft(order)} data-testid={`request-return-${order.id}`}>Claim</button>
            ) : null}
          </div>
        );
      })}

      {draft && (
        <form className="return-claim-form" onSubmit={submitReturn} data-testid="return-claim-form">
          <div className="return-form-head"><h3>Warranty Claim</h3><button type="button" onClick={() => setDraft(null)}><X size={16} /></button></div>
          <p className="profile-muted">{draft.order_number} · {draft.items?.[0]?.name}</p>
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason-select">
            <option value="defective">Device defective</option>
            <option value="damaged">Damaged device</option>
            <option value="wrong_item">Wrong item received</option>
            <option value="warranty_claim">Warranty issue</option>
            <option value="other">Other</option>
          </select>
          <textarea value={detail} onChange={(e) => setDetail(e.target.value)} minLength="8" placeholder="Issue describe karo..." required data-testid="return-detail-input" />
          <label>Photos (front & back)<input type="file" accept="image/*" multiple onChange={(e) => setPhotos([...e.target.files].slice(0, 2))} data-testid="return-photo-input" /></label>
          <label>Video (1 min)<input type="file" accept="video/*" onChange={(e) => setVideo(e.target.files?.[0] || null)} data-testid="return-video-input" /></label>
          <button className="primary-btn full" disabled={busy} data-testid="submit-return-claim-button">{busy ? "Submitting…" : "Submit Claim"}</button>
        </form>
      )}
    </div>
  );
}

function PolicyPage({ common }) {
  const { policy } = useParams();
  const pages = { shipping: ["Shipping policy", "Orders are processed after confirmed payment. Delivery updates and courier tracking appear in My Orders."], returns: ["Returns & refunds", "Request eligible returns from My Orders. Refunds are processed after device quality verification."], privacy: ["Privacy policy", "DMobileMart uses account, delivery and payment information only to process your orders and support requests."], terms: ["Terms of service", "Inventory and pricing can change. Orders are confirmed only after verified online payment or admin-approved direct transfer."] };
  const [title, description] = pages[policy] || pages.terms;
  return <><Topbar {...common} /><main className="simple-page policy-page" data-testid={`policy-page-${policy || "terms"}`}><span className="eyebrow">DMOBILEMART</span><h1>{title}</h1><p>{description}</p><section><h2>Customer support</h2><p>For help with payment, delivery, cancellation or privacy requests, contact support with your order number.</p></section></main><FooterBar /></>;
}

const HOMEPAGE_SECTION_LABELS = { "flash-deals": "Flash Deals", "mobile-parts-deals": "Mobile Parts Deals", "today-deals": "Today's Deals", "deal-of-the-day": "Deal of the Day", "new-stock": "New Stock", "stock-clearance": "Stock Clearance Sale" };

function CategoryPage({ products, onAdd, onWish, common, categories }) {
  const location = useLocation();
  const selected = new URLSearchParams(location.search).get("category") || "";
  const selectedSection = new URLSearchParams(location.search).get("section") || "";
  const searchQuery = new URLSearchParams(location.search).get("search") || "";
  const hasFilter = Boolean(selected || selectedSection);
  const isSearch = Boolean(searchQuery);
  const [view, setView] = useState("grid");
  const catIconMap = Object.fromEntries(NAV_CATEGORIES.map((c) => [c.slug, c.icon]));
  const tiles = categories.map((category, index) => { const matches = products.filter((product) => product.category_slug === category.slug); const CatIcon = catIconMap[category.slug] || Tag; return { ...category, count: matches.length, image: matches[0]?.image, CatIcon }; });
  return (
    <>
      <Topbar {...common} />
      <main className="simple-page category-hub" data-testid="category-hub">
        {!isSearch && <section className="category-hub-hero"><div><span className="eyebrow">DM MOBILE COLLECTIONS</span><h1>Shop by <em>Categories</em></h1><p>Find your perfect device, accessories and latest technology in one place.</p></div><div className="category-hero-art"><LayoutGrid size={96} /><span>LIVE</span></div></section>}
        <section className={`category-hub-layout${isSearch ? " search-mode" : ""}`}>
          {!isSearch && <aside className="category-rail" data-testid="category-sidebar">
            <b>All</b>
            <Link to="/category" className={!hasFilter ? "active" : ""} data-testid="category-all-link">
              <div className="rail-icon-wrap"><LayoutGrid size={18} /></div>
              <span className="rail-label">All</span>
              <span className="rail-count">{products.length}</span>
            </Link>
            {tiles.map((category) => {
              const RailIcon = category.CatIcon || Tag;
              return (
                <Link to={`/category?category=${category.slug}`} className={selected === category.slug ? "active" : ""} key={category.id} data-testid={`catalog-category-${category.slug}`}>
                  {category.image
                    ? <img className="rail-cat-icon" src={category.image} alt={category.name} onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling?.style?.setProperty("display", "flex"); }} />
                    : null}
                  <div className="rail-icon-wrap" style={{ display: category.image ? "none" : "flex" }}><RailIcon size={18} /></div>
                  <span className="rail-label">{category.name}</span>
                  <span className="rail-count">{category.count}</span>
                </Link>
              );
            })}
          </aside>}
          <section className="category-hub-main">
            <div className="category-hub-heading">
              <div>
                <span className="eyebrow">{isSearch ? "SEARCH RESULTS" : selectedSection ? "HOMEPAGE SALE SECTION" : "EXPLORE THE STORE"}</span>
                <h2 data-testid="catalog-section-heading">
                  {isSearch ? `Results for "${searchQuery}"` : selectedSection ? HOMEPAGE_SECTION_LABELS[selectedSection] || "Sale Collection" : selected ? tiles.find((tile) => tile.slug === selected)?.name || "Category" : "All Categories"}
                </h2>
              </div>
              <div className="catalog-view-toggle" data-testid="catalog-view-toggle"><button onClick={() => setView("grid")} className={view === "grid" ? "active" : ""} data-testid="catalog-grid-view-button"><LayoutGrid size={17} /></button><button onClick={() => setView("list")} className={view === "list" ? "active" : ""} data-testid="catalog-list-view-button"><Menu size={17} /></button></div>
            </div>
            {!isSearch && !hasFilter && <div className="category-tile-grid" data-testid="category-tile-grid">{tiles.map((category, index) => { const CIcon = category.CatIcon || Tag; return <Link to={`/category?category=${category.slug}`} className={`category-tile tile-${index % 6}`} key={category.id} data-testid={`category-tile-${category.slug}`}>{category.image ? <img src={category.image} alt={category.name} onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling?.style?.setProperty("display", "flex"); }} /> : null}<div className="category-tile-icon-fallback" style={{ display: category.image ? "none" : "flex" }}><CIcon size={40} /></div><div><b>{category.name}</b><small>{category.count} live products</small><span>Explore <ChevronRight size={14} /></span></div></Link>; })}</div>}
            <div className={`product-grid grid-4 category-products ${view === "list" ? "catalog-list-view" : ""}`} data-testid={`catalog-${view}-view`}>{products.map((product, index) => <ProductCard product={product} onAdd={onAdd} onWish={onWish} badge={BADGES[index % BADGES.length]} key={product.id} />)}</div>
            {!isSearch && hasFilter && products.length === 0 && (
              <div className="search-empty-state" data-testid="category-empty">
                <LayoutGrid size={40} />
                <h3>Is category mein abhi koi product nahi hai</h3>
                <p>Naye stock aate rehte hain — jald hi milenge</p>
                <Link to="/category" className="order-primary-button" style={{ marginTop: 8 }}>Saare categories dekho</Link>
              </div>
            )}
            {isSearch && products.length === 0 && <div className="search-empty-state" data-testid="search-empty"><Search size={40} /><h3>No results for "{searchQuery}"</h3><p>Try a different search term or browse categories</p></div>}
          </section>
        </section>
      </main>
      <FooterBar />
      <BottomNav {...common} active="Category" />
    </>
  );
}

function AdminRoute({ user }) { return user?.role === "admin" ? <AdminWorkspace /> : <Navigate to="/login?next=/admin" replace />; }

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [storefront, setStorefront] = useState(null);
  const [cart, setCart] = useState([]);
  const [wishCount, setWishCount] = useState(0);
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem("mobilecart-theme") || "dark");
  const lastAnnId = useRef(null);
  const onThemeChange = () => { const next = theme === "dark" ? "light" : "dark"; setTheme(next); localStorage.setItem("mobilecart-theme", next); document.body.dataset.theme = next; };
  const liveRefreshInFlight = useRef(false);
  const catalogParamsRef = useRef("");
  const catalogParams = useMemo(() => {
    if (location.pathname !== "/category") return "";
    const source = new URLSearchParams(location.search);
    const value = new URLSearchParams();
    if (source.get("category")) value.set("category", source.get("category"));
    if (source.get("search")) value.set("query", source.get("search"));
    if (source.get("sort")) value.set("sort", source.get("sort"));
    if (source.get("section")) value.set("section", source.get("section"));
    return value.toString() ? `?${value}` : "";
  }, [location.pathname, location.search]);
  const refreshProducts = useCallback(async (params) => { const activeParams = params === undefined ? catalogParamsRef.current : params; if (params !== undefined) catalogParamsRef.current = params; try { const connector = activeParams ? "&" : "?"; const { data } = await api.get(`/api/products${activeParams}${connector}_live=${Date.now()}`); setProducts(data.items.map(cardProduct)); } catch (error) { toast.error(apiError(error)); } }, []);
  const refreshAuctions = useCallback(async () => { try { const { data } = await api.get(`/api/auctions?_live=${Date.now()}`); setAuctions(data.map((entry) => ({ ...entry, product: entry.product ? cardProduct(entry.product) : null }))); } catch (error) { toast.error(apiError(error)); } }, []);
  const refreshAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/content/announcements?_live=${Date.now()}`);
      if (lastAnnId.current && data[0]?.id && data[0].id !== lastAnnId.current) {
        const newItems = [];
        for (const ann of data) {
          if (ann.id === lastAnnId.current) break;
          newItems.push(ann);
        }
        if (newItems.length > 0) {
          toast(`New Stock Just Arrived!`, {
            description: newItems[0].title.replace("New arrival: ", ""),
            icon: "📦",
            duration: 7000,
            action: { label: "View", onClick: () => window.location.href = "/category" },
          });
        }
      }
      lastAnnId.current = data[0]?.id || null;
      setAnnouncements(data);
    } catch { setAnnouncements([]); }
  }, []);
  const refreshStorefront = useCallback(async () => { try { const { data } = await api.get(`/api/storefront?_live=${Date.now()}`); setStorefront(data); } catch { setStorefront(null); } }, []);
  const refreshCart = useCallback(async () => { if (!user) { setCart([]); return; } try { const { data } = await api.get("/api/cart"); setCart(data.items.map((item) => ({ ...cardProduct(item.product), quantity: item.quantity, variant_sku: item.variant_sku }))); } catch (error) { if (error.response?.status !== 401) toast.error(apiError(error)); } }, [user]);
  const refreshWish = useCallback(async () => { if (!user) { setWishCount(0); return; } try { const { data } = await api.get("/api/wishlist"); setWishCount(data.length); } catch { setWishCount(0); } }, [user]);
  const refreshOrderCount = useCallback(async () => { if (!user) { setActiveOrderCount(0); return; } try { const { data } = await api.get("/api/orders?page=1&per_page=50"); const active = (data.items || []).filter(o => !["delivered","cancelled","refunded"].includes(o.status)); setActiveOrderCount(active.length); } catch { setActiveOrderCount(0); } }, [user]);
  const refreshUser = useCallback(async () => { try { const data = await restoreSession(); setUser(data); return data; } catch { return null; } }, []);
  useEffect(() => { document.body.dataset.theme = theme; }, [theme]);
  useEffect(() => { const clearExpiredSession = () => { setUser(null); setCart([]); setWishCount(0); }; window.addEventListener("mobilecart:session-expired", clearExpiredSession); return () => window.removeEventListener("mobilecart:session-expired", clearExpiredSession); }, []);
  useEffect(() => { restoreSession().then((data) => setUser(data)).catch(() => setUser(null)).finally(() => setAuthLoading(false)); api.get(`/api/categories?_live=${Date.now()}`).then(({ data }) => setCategories(data)).catch(() => setCategories([])); refreshAuctions(); refreshAnnouncements(); refreshStorefront(); }, [refreshAuctions, refreshAnnouncements, refreshStorefront]);
  useEffect(() => { refreshProducts(catalogParams); }, [catalogParams, refreshProducts]);
  useEffect(() => {
    let refreshQueued = false;
    let disposed = false;
    const liveRefresh = async () => {
      if (liveRefreshInFlight.current) { refreshQueued = true; return; }
      liveRefreshInFlight.current = true;
      try {
        await Promise.all([
          refreshProducts(), refreshAuctions(), refreshAnnouncements(), refreshStorefront(),
          api.get(`/api/categories?_live=${Date.now()}`).then(({ data }) => setCategories(data)).catch(() => {}),
        ]);
      } finally {
        liveRefreshInFlight.current = false;
        if (refreshQueued && !disposed) { refreshQueued = false; liveRefresh(); }
      }
    };
    const liveSync = setInterval(() => { if (document.visibilityState === "visible") liveRefresh(); }, 20000);
    const visibilitySync = () => { if (document.visibilityState === "visible") liveRefresh(); };
    const storageSync = (event) => { if (event.key === "mobilecart-live-update") liveRefresh(); };
    window.addEventListener("mobilecart:live-update", liveRefresh);
    document.addEventListener("visibilitychange", visibilitySync);
    window.addEventListener("storage", storageSync);
    return () => { disposed = true; clearInterval(liveSync); window.removeEventListener("mobilecart:live-update", liveRefresh); document.removeEventListener("visibilitychange", visibilitySync); window.removeEventListener("storage", storageSync); };
  }, [refreshProducts, refreshAuctions, refreshAnnouncements, refreshStorefront]);
  useEffect(() => { if (!authLoading) { refreshCart(); refreshWish(); refreshOrderCount(); } }, [authLoading, user, refreshCart, refreshWish, refreshOrderCount]);
  const add = async (product, buyNow = false, variant_sku = null) => { if (product.stock <= 0) { toast.error("This product is out of stock"); return; } if (!user) { toast.error("Please sign in to save your cart"); navigate("/login?next=/cart"); return; } try { await api.post("/api/cart/items", { product_id: product.id, quantity: 1, variant_sku }); await refreshCart(); toast.success(`${product.name} added to cart`); speakHindi(`${product.name} कार्ट में जोड़ दिया गया`); if (buyNow) navigate("/cart"); } catch (error) { toast.error(apiError(error)); } };
  const setQuantity = async (item, quantity) => { try { if (quantity < 1) await api.delete(`/api/cart/items/${item.id}`); else await api.patch(`/api/cart/items/${item.id}`, { product_id: item.id, quantity }); await refreshCart(); } catch (error) { toast.error(apiError(error)); } };
  const remove = async (id) => { try { await api.delete(`/api/cart/items/${id}`); await refreshCart(); toast.success("Removed from cart"); speakHindi("कार्ट से हटा दिया गया"); } catch (error) { toast.error(apiError(error)); } };
  const wish = async (id) => { if (!user) { navigate("/login"); return; } try { await api.put(`/api/wishlist/${id}`); await refreshWish(); toast.success("Saved to wishlist"); speakHindi("विशलिस्ट में सेव कर दिया गया"); } catch (error) { toast.error(apiError(error)); } };
  const logout = async () => { try { await api.post("/api/auth/logout"); } finally { setUser(null); setCart([]); setWishCount(0); navigate("/"); toast.success("Signed out"); } };
  const common = { cartCount: cart.reduce((sum, item) => sum + item.quantity, 0), wishCount, activeOrderCount, user, onSearch: (query) => refreshProducts(`?query=${encodeURIComponent(query)}`), onMenu: () => navigate("/account") };
  if (authLoading) return <Loading label="Connecting to DMobileMart…" />;
  return (
    <>
      <Toaster theme={theme} position="bottom-right" />
      <Routes>
        <Route path="/auth/google" element={<GoogleCallback setUser={setUser} />} />
        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/admin/*" element={<AdminRoute user={user} />} />
        <Route path="/product/:id" element={<ProductPage onAdd={add} onWish={wish} common={common} />} />
        <Route path="/cart" element={user ? <CartPage cart={cart} onAdd={add} onSet={setQuantity} onRemove={remove} common={common} /> : <Navigate to="/login?next=/cart" replace />} />
        <Route path="/checkout" element={user ? <Checkout cart={cart} user={user} refreshUser={refreshUser} refreshCart={refreshCart} common={common} /> : <Navigate to="/login?next=/checkout" replace />} />
        <Route path="/my-orders" element={user ? <><Topbar {...common} /><CustomerOrders /><FooterBar /><BottomNav {...common} active="Account" /></> : <Navigate to="/login?next=/my-orders" replace />} />
        <Route path="/my-orders/:id" element={user ? <><Topbar {...common} /><CustomerOrderDetail /><FooterBar /><BottomNav {...common} active="Account" /></> : <Navigate to="/login?next=/my-orders" replace />} />
        <Route path="/auctions" element={<Auctions auctions={auctions} user={user} refreshAuctions={refreshAuctions} common={common} />} />
        <Route path="/auctions/won" element={<AuctionWinnerPayment user={user} common={{ ...common, Topbar, FooterBar, BottomNav, Loading }} />} />
        <Route path="/sell" element={<SellPageNew common={{ ...common, Topbar, FooterBar, BottomNav, user }} />} />
        <Route path="/account" element={user ? <Account user={user} setUser={setUser} common={common} theme={theme} onThemeChange={onThemeChange} refreshUser={refreshUser} onAdd={add} refreshWish={refreshWish} /> : <Navigate to="/login?next=/account" replace />} />
        <Route path="/policies/:policy" element={<PolicyPage common={common} />} />
        <Route path="/category" element={<CategoryPage products={products} onAdd={add} onWish={wish} common={common} categories={categories} />} />
        <Route path="*" element={<FuturisticHome products={products} auctions={auctions} announcements={announcements} homeConfig={storefront} onAdd={add} onWish={wish} common={common} Topbar={Topbar} FooterBar={FooterBar} BottomNav={BottomNav} Loading={Loading} />} />
      </Routes>
      <CustomerLiveChat user={user} hidden={location.pathname === "/login" || location.pathname === "/register"} />
    </>
  );
}

export default function Root() { return <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}><BrowserRouter><App /></BrowserRouter></GoogleOAuthProvider>; }
